import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Send, Loader2, Shield, RotateCcw, BookOpen, FileText, FilePlus, BookMarked, Scale, FileSignature, ThumbsUp, Brain, FileDown, Square, Pencil, Check, ListTree, Save, Globe, Copy } from 'lucide-react';
import { ArkoMascotAvatar } from './portal/ArkoMascotAvatar';
import { PortalChip } from './arkorbit-ui/PortalChip';
import { PortalChipBar, type PortalChipBarHandle } from './arkorbit-ui/PortalChipBar';
import {
  CHAT_ACCENT,
  CURSOR_CHAT,
  CURSOR_PORTAL,
  PORTAL_CHAT,
  type ChatAccentColor,
  type UiAccent,
} from './arkorbit-ui/portalTheme';
import { CONTRACT_TEMPLATES } from './contract/contractTemplates';
import {
  ARKO_OPEN_CONTRACT_CREATOR_EVENT,
  ContractFlowState,
  EMPTY_CONTRACT_FLOW,
  advanceContractFlow,
  inferContractTemplateId,
  looksLikeContractRequest,
  startContractFlow,
} from './contract/contractChatFlow';
import { displayArkoTurnText } from './portal/arkoVisibleLanguage';
import { ArkoResponseProof, formatBotMessageForDisplay, hasArkoProof, LexJudJoinFolds, LexVerifiedQuoteBlock, WebRechercheFold } from './portal/ArkoBotSources';
import { ArkoAnswerLabels } from './portal/ArkoAnswerLabels';
import { hasLexQuoteBlock, parseLexProof, type LexProof } from './portal/lexProof';
import { consumeArkoSse } from './portal/arkoStreamClient';
import { parseJudicatureProof, type JudicatureProof } from './portal/judicatureProof';
import { judSearchLookupRequest } from './portal/portalJudSearchChip';
import { webSearchLookupRequest } from './portal/portalWebSearchChip';
import { parseWebSearchProof, type WebSearchProof } from './portal/webSearchProof';
import {
  PORTAL_CHAT_SCROLL_FOLLOW_MARKER,
  followAfterNewUserBubble,
  followAfterUserScroll,
  shouldApplyStreamFollow,
  shouldDetachFollowForKey,
  shouldDetachFollowForWheel,
} from './portal/portalChatScrollFollow';
import {
  PORTAL_LEX_JUD_JOIN_HEADNOTE_MARKER,
  PORTAL_LEX_JUD_JOIN_FRAGEPUNKT_MARKER,
  PORTAL_LEX_JUD_JOIN_MARKER,
  PORTAL_LEX_JUD_JOIN_NO_GHOST_MARKER,
  PORTAL_LEX_JUD_JOIN_TOPIC_MARKER,
  PORTAL_LEX_JUD_JOIN_VOICE_MARKER,
  applyJoinTopicToJudProof,
  buildLexJudJoinSynthesis,
  buildOghFollowupPrompt,
  isPremiumPortalCaller,
  lexCarryNormIds,
  sanitizeLexCarry,
  shouldAutoOghJoin,
  shouldRenderLexQuoteAsLead,
  shouldShowOghFollowupUserBubble,
  type LexCarry,
} from './portal/portalOghFollowup';
import { ARKO_INTENT_EVENT, type ArkoHubIntentDetail } from './portal/arkoHubModes';
import {
  ARKO_AKTE_FUEHREN_EVENT,
  PORTAL_AKTE_VARIANT_EXTRACT_MARKER,
  buildAkteVariantDeepenPrompt,
  parseAkteSelectedExtractsFromDocumentContext,
  type AkteFuehrenPrompt,
} from './portal/portalAkteFuehren';
import type { ArkoHubMode } from './portal/arkoHubModes';
import {
  MEMCORE_SAVE_LABEL,
  PORTAL_AKTE_CHIP_SINK_MARKER,
  parseSuggestedActions,
  resolveSaveToFileChipSink,
  type ArkoQuickActionType,
  type SuggestedAction,
} from './portal/portalArkoChipSink';
import {
  EMPTY_GUIDED_FLOW,
  advanceGuidedFlow,
  continueGuidedFlowAfterWebhook,
  isGuidedFlowId,
  startGuidedFlow,
  type GuidedFlowBotBlock,
  type GuidedFlowChip,
  type GuidedFlowState,
  type GuidedRiskFinding,
} from './portal/arkoGuidedFlows';
import { PortalGuidedRiskPanel } from './portal/PortalGuidedRiskPanel';
import { PortalMandatsConfirmCard } from './portal/PortalMandatsConfirmCard';
import {
  applyMandatsConfirmEdits,
  type MandatsannahmeSlots,
} from './portal/parseMandatsSlots';
import { createPortalAkteVertragBeratung } from './portal/portalAkten';
import { ArkoCursorComposer, type ArkoCursorComposerHandle } from './portal/ArkoCursorComposer';
import { ARKO_CHAT_COLUMN_CLASS } from './portal/portalArkoChatWidth';
import { AkteHandoffBanner } from './portal/MemoryDialogs';
import { AKTE_HANDOFF_DOCS_HINT, prefixSavedChatStatus, savedChatStand } from './portal/portalArkoThreadBind';
import { dropDeferredAnswerSection, mergeDeferredChip, splitPortalTasks } from './portal/portalArkoTaskSplit';
import {
  ATTACHED_PARAGRAPH_CONTEXT_HEAD,
  buildAttachedParagraphContext,
  stripAttachedParagraphPrefix,
  type ParagraphRef,
} from './portal/portalParagraphRef';
import { relatedParagraphRefsFromLexProof } from './portal/portalRelatedNorms';
import {
  buildAnalyzeChatInput,
  buildAnalyzeUserDisplayText,
  prepareChatAnalyzeDocumentContext,
} from './portal/portalChatDocumentAttach';
import {
  reportArkoHttpError,
  reportArkoQualityGateReject,
} from './portal/portalTelemetry';
import {
  type MemProofItem,
  type MemVermerk,
  type MemcoreContext,
  MEM_RECALL_CHIP_HINT,
  MEM_RECALL_CHIP_LABEL,
  MEM_RECALL_CHIP_PREFIX,
  MEM_RECALL_LEGACY_PREFIX,
  listMeineVermerke,
  memcoreRecall as runMemcoreRecall,
  shouldMemcoreRecall,
  visibleReplyMemContext,
} from './portal/memcore';
import {
  PortalParagraphContextChip,
  PortalParagraphHitlPreview,
} from './portal/PortalParagraphHitlPreview';
import {
  buildParagraphIndexMap,
  parseStructuredDocumentEdit,
  paragraphEntryToCursorSelection,
  resolveParagraphReference,
  type CursorSelection,
  type ParagraphIndexEntry,
  type StructuredDocumentEdit,
} from './portal/portalParagraphMap';
import { ArkoReasoningTraceCard } from './portal/ArkoReasoningTraceCard';
import { ArkoMessageFeedback } from './portal/ArkoMessageFeedback';
import {
  isPortalDemoSupportEnabled,
  readPortalDemoSupportContext,
  type FeedbackDiagMeta,
} from './portal/portalCustomerFeedback';
import { ArkoThinkingProgress } from './portal/ArkoThinkingProgress';
import type { ThinkingContext } from './portal/arkoThinkingStages';
import { ArkoChatAiActNotice } from './portal/ArkoChatAiActNotice';
import { parseApprovalFristFromPayload } from './portal/verfahrensmotor/parseApprovalFrist';
import { FristApprovalCard } from './portal/verfahrensmotor/FristApprovalCard';
import { parseReasoningTrace, getAnswerPathStyleFromTrace, type ReasoningNarrativeFacts, type ReasoningTrace } from './portal/reasoningTrace';
import { classifyArkoTrust } from './portal/arkoTrust';
import {
  hasJudicatureProofData,
  isVerifiedJudicatureProof,
  type JudicatureProof,
} from './portal/judicatureProof';
import { hasWebSearchProofData, type WebSearchProof } from './portal/webSearchProof';
import type { LexProof } from './portal/lexProof';
import type { ApprovalFristPayload } from './portal/verfahrensmotor/fristApprovalTypes';
import { isExplicitPortalFristQuestion } from './portal/portalDocumentFristProbe';
import {
  buildDocumentWelcomeActions,
  buildDocumentWelcomeText,
  DOCUMENT_QUESTION_INPUT_PLACEHOLDER,
  hasDocumentWelcomeBeenShown,
  isDocumentWelcomeGreeting,
  markDocumentWelcomeShown,
  resolveDocumentQuestionSendIntent,
  type DocumentWelcomeAction,
  type DocumentWelcomeActionId,
} from './portal/portalDocumentWelcome';
import {
  ARKO_WEB_ARTICLE_FETCH_URL,
  getPortalPocketBaseUrl,
  getPortalPbToken,
  portalAuthzHeaders,
} from '../constants';
import {
  buildWebArticleDocumentContext,
  detectWebArticleRequest,
  fetchWebArticle,
  formatWebArticlePreview,
  webArticleMetaForTrace,
  type WebArticleFetchResult,
} from './portal/arkoWebArticle';
import {
  ArkoWebArticleActions,
  type WebArticleActionId,
} from './portal/ArkoWebArticleActions';
import {
  buildAkteTurnContextPayload,
  type PortalOpenDocumentMeta,
  type PortalSelectedDocumentPayload,
  type PortalDocumentOpenFields,
} from './portal/portalDocuments';
import {
  hasUnfilledSchreibenPlaceholders,
  portalDocumentKindLabel,
  sanitizeSchreibenExportText,
  schreibenExportDisplayName,
} from './portal/schreibenDraft';

/** Badge und Nachweise desselben Laufs — die Spur liest das, nicht eine Schablone. */
function reasoningFactsForTurn(args: {
  answerText: string;
  lexProof?: LexProof | null;
  judProof?: JudicatureProof | null;
  webProof?: WebSearchProof | null;
}): ReasoningNarrativeFacts {
  const trust = classifyArkoTrust(args.answerText, args.lexProof);
  const verified = trust.level === 'verified' && trust.normIds.length > 0;
  const webHit = args.webProof?.status === 'ok' && (args.webProof.sources?.length ?? 0) > 0;
  let webSource = '';
  if (webHit) {
    const url = String(args.webProof?.sources?.[0]?.url || '').trim();
    try {
      webSource = new URL(url).host;
    } catch {
      webSource = '';
    }
  }
  return {
    badgeKnown: true,
    verifiedNormIds: verified ? [...trust.normIds] : [],
    normSource: verified ? (trust.risUrl ? 'RIS' : 'Normbestand') : undefined,
    answerText: args.answerText,
    judicatureRan: hasJudicatureProofData(args.judProof) ? true : undefined,
    judicatureHit: hasJudicatureProofData(args.judProof)
      ? isVerifiedJudicatureProof(args.judProof)
      : undefined,
    webRan: hasWebSearchProofData(args.webProof) ? true : undefined,
    webHit: hasWebSearchProofData(args.webProof) ? webHit : undefined,
    webSource: webSource || undefined,
  };
}

const CHAT_DRAFT_PREFIX = 'arkorbit_portal_chat_draft_v1__';
const CHAT_DRAFT_DEBOUNCE_MS = 400;

type ArkoRequestIntent =
  | QuickAction['intent']
  | 'frist_vorschlag'
  | 'document_question'
  | 'judicature_recherche'
  | 'web_recherche'
  | 'schreiben_erstellen';

function chatDraftKey(customerId: string): string {
  return `${CHAT_DRAFT_PREFIX}${customerId}`;
}

interface QuickAction {
  label: string;
  icon: React.ElementType;
  prefix: string;
  intent: 'umformulieren' | 'erklaeren' | 'zusammenfassen' | 'speichern' | 'rechtsfrage';
}

const QUICK_ACTIONS: QuickAction[] = [
  { label: 'Umformulieren', icon: RotateCcw,   prefix: 'Bitte formuliere folgenden Text professionell um:\n\n', intent: 'umformulieren' },
  { label: 'Erklären',      icon: BookOpen,    prefix: 'Erkläre mir bitte folgendes verständlich:\n\n',          intent: 'erklaeren' },
  { label: 'Zusammenfassen',icon: FileText,    prefix: 'Fasse folgenden Text präzise zusammen:\n\n',             intent: 'zusammenfassen' },
  { label: 'Speichern',     icon: BookMarked,  prefix: 'Bitte speichere folgende Information in deinem Gedächtnis:\n\n', intent: 'speichern' },
];

/** LEXCORE-Pfad in n8n nur bei `intent: rechtsfrage` (Slice 2). */
const RECHTSFRAGE_ACTION: QuickAction = {
  label: 'Rechtsfragen',
  icon: Scale,
  prefix: 'Rechtsfrage (verifizierte Normen aus LEXCORE):\n\n',
  intent: 'rechtsfrage',
};

const ALL_INTENT_QUICK_ACTIONS: QuickAction[] = [...QUICK_ACTIONS, RECHTSFRAGE_ACTION];

export const ARKO_INTENT_LABELS: Record<QuickAction['intent'], string> = {
  umformulieren: 'Umformulieren',
  erklaeren: 'Erklären',
  zusammenfassen: 'Zusammenfassen',
  speichern: 'Speichern',
  rechtsfrage: 'Rechtsfragen',
};

export function arkoIntentLabel(intent: QuickAction['intent'] | '' | null | undefined): string | null {
  if (!intent) return null;
  return ARKO_INTENT_LABELS[intent] ?? null;
}

function stripQuickActionPrefix(text: string): string {
  for (const action of ALL_INTENT_QUICK_ACTIONS) {
    if (text.startsWith(action.prefix)) {
      return text.slice(action.prefix.length);
    }
  }
  return stripAttachedParagraphPrefix(text);
}

function stripMemRecallPrefix(text: string): string {
  for (const prefix of [MEM_RECALL_CHIP_PREFIX, MEM_RECALL_LEGACY_PREFIX]) {
    if (text.startsWith(prefix)) return text.slice(prefix.length);
  }
  return text;
}

const ARKO_TOOL_HINTS: Record<string, string> = {
  Umformulieren: 'Formuliert den Text um.',
  Erklären: 'Erklärt den Text in Alltagssprache.',
  Zusammenfassen: 'Fasst den Text kurz zusammen.',
  Speichern: 'Merkt den Text als Notiz.',
  Rechtsfragen: 'Schlägt im Normenbestand nach.',
  'Als Rechtsfrage prüfen?': 'Prüft die letzte Frage noch einmal im Normenbestand.',
  'Vertrag erstellen': 'Fragt zuerst, welchen Vertrag. Danach öffnet sich der Ersteller.',
};

// F2 (Andreas-Entscheid 15.05.2026): Spiegel der serverseitigen Heuristik in
// `01_n8n_Workflows/ARKO_AGENT.json` (Node "Portal Intent Heuristik").
// Wird ein eindeutiges §-/Norm-Muster erkannt, hat n8n bereits auf den
// LEXCORE-Pfad geroutet — der UI-Chip "Als Rechtsfrage prüfen?" ist dann nicht
// nötig. Nur bei mehrdeutigem Text bekommt der Nutzer den Chip angeboten.
// Sync mit Portal Intent Heuristik (arko-portal-n8n-code.js) — § ohne Ziffer + LEXCORE-Wunsch.
const LEGAL_SECTION_RE = /(?:§{1,2}\s*\d+|\bparagraph(?:en)?\s+\d+|§\s*zum\s+thema|wichtigsten\s+§{1,2})/i;
const LEGAL_LAW_RE = /\b(ABGB|MRG|StGB|StPO|UrhG|UStG|EStG|KStG|GmbHG|AktG|BAO|VStG|GewO|StVO|KSchG|EheG|EO|UGB|HGB|FBG|RAO|VVG|GBG|AVG|WGG|MaklerG|JN|ZPO|UWG|DSG|DSGVO)\b/i;
const LEGAL_KEYWORD_RE = /\b(paragraf(?:en)?|paragraph(?:en)?|lexcore|normen?|welche\s+paragraphen|aus\s+lexcore|in\s+lexcore|nachschlag(?:en)?)\b/i;

function looksLikeLegalQuestion(text: string): boolean {
  return LEGAL_SECTION_RE.test(text) || LEGAL_LAW_RE.test(text) || LEGAL_KEYWORD_RE.test(text);
}

/**
 * Stream 5 — Proaktive Quick-Actions: kontextuelle Aktions-Chips, die das
 * Backend (`arko-portal-n8n-code.js`, Feld `suggestedActions`) nach jeder ARKO-
 * Antwort vorschlägt. Jeder Chip löst einen BESTEHENDEN Flow aus, kein neuer
 * Endpoint.
 */
export type { ArkoQuickActionType, SuggestedAction } from './portal/portalArkoChipSink';
export { parseSuggestedActions } from './portal/portalArkoChipSink';

const ARKO_QUICK_ACTION_ICONS: Record<ArkoQuickActionType, React.ElementType> = {
  lookup_law: Scale,
  ogh_dazu: Scale,
  web_recherche: Globe,
  export_pdf: FileDown,
  save_to_file: Save,
  refine_letter: FileSignature,
  schreiben_erstellen: FileSignature,
  pick_variant: ListTree,
  variant_1: ListTree,
  variant_2: ListTree,
  variant_3: ListTree,
};

export interface Message {
  role: 'user' | 'bot';
  text: string;
  /** Portal-Message-ID — MEMCORE `quelle_ref` (Daumen-Freigabe, §7.7). Optional für Altbestand. */
  id?: string;
  /** Verfahrensmotor Slice 1: strukturierter Nachrichtentyp (Default: Fließtext). */
  messageKind?: 'text' | 'approval_frist' | 'document_welcome' | 'document_focus_switch' | 'web_article' | 'guided_flow' | 'background_analysis';
  /** Hintergrundanalyse: voller Worker-Text hinter der Kurzzusammenfassung. */
  backgroundAnalysis?: {
    documentName: string;
    details: string | null;
  };
  /** Phase 1.3: Auswahl-Chips im geführten Workflow. */
  guidedFlowChips?: GuidedFlowChip[];
  /** Phase 1.3: Strukturierte Risiko-Ampel. */
  guidedRiskFindings?: GuidedRiskFinding[];
  /** Phase 1.3: Labor-Demo-Hinweis (MRG ohne LEXCORE). */
  guidedLaborDemo?: boolean;
  /** Confirm-Step Mandatsannahme: drei Slots als Karte, nicht als Markdown. */
  mandatsConfirm?: MandatsannahmeSlots;
  /** Web-Artikel-Fetch: geladener Artikel + Action-Chips. */
  webArticle?: WebArticleFetchResult;
  webArticleUserIntent?: string;
  /** Portal: Begrüßung mit Dokument-Kontext — Action-Chips ohne Auto-Analyse. */
  documentWelcomeActions?: DocumentWelcomeAction[];
  /** Verfahrensmotor Slice 1: Frist-Approval-Card (synchron mit Dokument-Spiegel via `card_id`). */
  approvalFrist?: ApprovalFristPayload;
  /** Modul C: Recall-Nachweis (§7.5 — ausklappbar, nicht im Fließtext). */
  memProof?: MemProofItem[];
  /** LEXCORE-Nachweis aus n8n (ausklappbar: Quellen, Warum, Wann). */
  lexProof?: LexProof | null;
  /** Stream 3: Bot-Antwort ist ein Schreiben-/Schriftsatz-Entwurf -> PDF-Export-Button. */
  schreibenDraft?: boolean;
  /**
   * Nach PDF-Export: Akte-Datei im Chat als klickbare Karte (Portal-Viewer öffnen).
   * document_url intern — nie als nackte Nextcloud-URL im Fließtext.
   */
  portalDocumentOpen?: PortalDocumentOpenFields;
  /** Slice 1.3b V2: Multi-Doc-Synthese-Antwort → „In Dokument übernehmen“. */
  synthesisDraft?: boolean;
  /** Stream 5: Proaktive Quick-Action-Chips, die das Backend zur Antwort vorschlägt. */
  suggestedActions?: SuggestedAction[];
  /** Slice 1.2: deterministisches Routing (kein rohes CoT). */
  reasoningTrace?: ReasoningTrace | null;
  /** Premium: deterministische OGH-Treffer, strikt getrennt vom LEXCORE-Nachweis. */
  judProof?: JudicatureProof | null;
  /** Internet-Recherche (ARKORBIT-Gate), URLs in der Klappe. */
  webProof?: WebSearchProof | null;
  /** Fall-Join: eine ARKO-Stimme; Originale nur in Klappen, nicht als zweiter Turn. */
  lexJudJoinVoice?: boolean;
  /** Phase 1.4: strukturierter Absatz-Edit-Vorschlag (HITL). */
  structuredEdit?: StructuredDocumentEdit | null;
  /** Phase 1.4: aufgelöste Absatz-Referenz im User-Text. */
  paragraphRef?: ParagraphIndexEntry | null;
  /** Streaming: Badge solange Quality Gate aussteht (z. B. „wird geprüft“). */
  streamBadge?: string | null;
  /** Streaming: Draft noch nicht final (Gate pending). */
  streamDraft?: boolean;
}

const ARKO_TIMEOUT_USER_MESSAGE =
  'ARKO denkt noch nach… Das Modell lädt gerade. Bitte nochmals senden.';
const ARKO_CONNECTIVITY_USER_MESSAGE =
  'ARKO ist gerade nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.';

function isArkoSystemNotice(text: string): boolean {
  const t = text.trim();
  return t === ARKO_TIMEOUT_USER_MESSAGE || t === ARKO_CONNECTIVITY_USER_MESSAGE;
}

function mkGuidedFlowMsg(
  block: GuidedFlowBotBlock,
  extras?: {
    reasoningTrace?: ReasoningTrace | null;
    lexProof?: LexProof | null;
    judProof?: JudicatureProof | null;
    webProof?: WebSearchProof | null;
    structuredEdit?: StructuredDocumentEdit | null;
  },
): Message {
  return {
    role: 'bot',
    text: block.text,
    id: newMessageId(),
    messageKind: 'guided_flow',
    ...(block.chips?.length ? { guidedFlowChips: block.chips } : {}),
    ...(block.riskFindings?.length ? { guidedRiskFindings: block.riskFindings } : {}),
    ...(block.laborDemo ? { guidedLaborDemo: true } : {}),
    ...(block.mandatsConfirm ? { mandatsConfirm: block.mandatsConfirm } : {}),
    ...(extras?.reasoningTrace ? { reasoningTrace: extras.reasoningTrace } : {}),
    ...(extras?.lexProof ? { lexProof: extras.lexProof } : {}),
    ...(extras?.judProof ? { judProof: extras.judProof } : {}),
    ...(extras?.webProof ? { webProof: extras.webProof } : {}),
    ...(extras?.structuredEdit ? { structuredEdit: extras.structuredEdit } : {}),
  };
}

function mkMsg(
  role: Message['role'],
  text: string,
  memProof?: MemProofItem[],
  lexProof?: LexProof | null,
  schreibenDraft?: boolean,
  suggestedActions?: SuggestedAction[],
  approvalFrist?: ApprovalFristPayload | null,
  reasoningTrace?: ReasoningTrace | null,
  synthesisDraft?: boolean,
  paragraphRef?: ParagraphIndexEntry | null,
  structuredEdit?: StructuredDocumentEdit | null,
  judProof?: JudicatureProof | null,
  webProof?: WebSearchProof | null,
): Message {
  return {
    role,
    text,
    id: newMessageId(),
    ...(approvalFrist
      ? { messageKind: 'approval_frist' as const, approvalFrist }
      : {}),
    ...(memProof && memProof.length > 0 ? { memProof } : {}),
    ...(lexProof ? { lexProof } : {}),
    ...(schreibenDraft ? { schreibenDraft: true } : {}),
    ...(suggestedActions && suggestedActions.length > 0 ? { suggestedActions } : {}),
    ...(reasoningTrace ? { reasoningTrace } : {}),
    ...(synthesisDraft ? { synthesisDraft: true } : {}),
    ...(paragraphRef ? { paragraphRef } : {}),
    ...(structuredEdit ? { structuredEdit } : {}),
    ...(judProof ? { judProof } : {}),
    ...(webProof ? { webProof } : {}),
  };
}

function newArkoRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'req_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

type MessagesUpdater = React.Dispatch<React.SetStateAction<Message[]>>;

interface InlineChatProps {
  title: string;
  subtitle: string;
  initialMessage: string;
  webhookUrl: string;
  accentColor?: ChatAccentColor;
  mode?: string;
  extraBody?: Record<string, unknown>;
  /** Dokument-Kontext (n8n); wird nur bei `documentContextEnabled` und ohne aktiven Vertrags-Flow gesendet. */
  documentContext?: string | null;
  documentContextEnabled?: boolean;
  /** Guard: Fingerprint des aktuellen Extract-Standes (sessionStorage). */
  documentExtractFingerprint?: string | null;
  /** Guard: Auto-Analyse für diesen Extract-Stand bereits erledigt. */
  documentAnalyzeDone?: boolean;
  /** Anzeigename des geöffneten Dokuments (Willkommens-Chips). */
  documentDisplayName?: string | null;
  /** Slice 1.3: Fokussiertes Dokument — Welcome-Chips pro Doc (sessionStorage). */
  activeDocumentId?: string | null;
  /** Slice 1.3: Metadaten offener Editor-Tabs → Webhook `openDocuments[]`. */
  openDocumentsMeta?: PortalOpenDocumentMeta[];
  /** Slice 1.3b: Multi-Dokument-Synthese (2+ gewählt). */
  selectedDocuments?: PortalSelectedDocumentPayload[];
  /** Slice 1.3b: Chat-Hinweis „ARKO nutzt: A + B“. */
  synthesisDocumentLabels?: string | null;
  /** PII-freier Diagnose-Bool: fokussiertes Dokument ist einer Akte zugeordnet. */
  feedbackAkteBound?: boolean;
  timeoutMs?: number;
  /** Zeigt Quick-Action Buttons (Umformulieren, Erklären, …) */
  showQuickActions?: boolean;
  /** Fünfte Aktion „Rechtsfragen“ (`intent: rechtsfrage`) — Anwalts-ARKO + LEXCORE in n8n */
  showRechtsfrageQuickAction?: boolean;
  /**
   * Controlled-Mode: Wenn `messages` UND `onMessagesChange` gesetzt sind, übernimmt der Parent
   * die Hoheit über den Verlauf (z. B. für persistierte Multi-Tab-Chats). Sonst hält die
   * Komponente den Verlauf intern (Default für Affiliate / Dashboard).
   */
  messages?: Message[];
  onMessagesChange?: MessagesUpdater;
  /** Once-UI-inspirierte Pill-Chips statt flacher Quick-Action-Buttons */
  usePortalChips?: boolean;
  chipAccent?: UiAccent;
  /** Donna-Pixel-Bubble pulsiert mit, wenn gesetzt */
  onLoadingChange?: (loading: boolean) => void;
  /** Ersetzt Standard-Sparkles-Avatar im Header */
  headerAvatar?: React.ReactNode;
  /** DonnaPanel o.ä. — eigener äußerer Header */
  hideHeader?: boolean;
  /** Schmale Split-View: weniger Padding in Nachrichten + Eingabe. */
  compactChrome?: boolean;
  shellClassName?: string;
  /** Stream D: Vertragsgenerator-Slot-Filling im Chat aktivieren (Anwalts-ARKO). */
  enableContractIntent?: boolean;
  /** Ruft den Generate-Endpoint und liefert die Chat-Antwort (Editor-Link). */
  onContractGenerate?: (args: {
    templateType: string;
    fields: Record<string, unknown>;
    displayName: string;
  }) => Promise<{ ok: boolean; message: string }>;
  /**
   * Stream 3: Schreiben-/Schriftsatz-Entwurf als PDF exportieren. Wird der
   * Callback gesetzt UND markiert n8n eine Bot-Antwort als Entwurf
   * (`schreibenDraft`), zeigt der Chat unter der Antwort einen PDF-Export-Button.
   */
  onExportSchreibenPdf?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  /**
   * Schreiben-Entwurf als DOCX in der Akte ablegen und im Portal-Viewer öffnen
   * (OnlyOffice, kein Nextcloud-Link).
   */
  onExportSchreibenDocx?: (args: {
    text: string;
    displayName: string;
  }) => Promise<{
    ok: boolean;
    message: string;
    portalDocument?: PortalDocumentOpenFields;
  }>;
  /** Gebundene Akte vorhanden — „In Akte speichern“ schreibt dorthin, nicht ins MEMCORE. */
  akteBound?: boolean;
  /** Sichtbarer Arbeitsstand des Weiterarbeiten-Chats (kein Verlauf). */
  akteHandoff?: string | null;
  /** Kreis voll / Session: Handoff verdichten, neuer Chat an derselben Akte. */
  onContinueAkte?: () => void;
  /** Datei-Karte → Portal-Viewer/OnlyOffice (kein Nextcloud-Tab). */
  onOpenPortalDocument?: (fields: PortalDocumentOpenFields) => void | Promise<void>;
  /**
   * Slice 1.3b V2: Synthese-Entwurf ins geöffnete oder neues Dokument übernehmen.
   */
  onApplySynthesisToDocument?: (args: {
    text: string;
    target: 'existing' | 'new';
  }) => Promise<{ ok: boolean; message: string }>;
  /** Slice 1.3b V2: Dokument im Editor geöffnet → beide Übernahme-Buttons. */
  synthesisHasOpenDocument?: boolean;
  /** Portal: Entwurf in sessionStorage puffern (pro Kunde). */
  customerId?: string;
  /** Parent-Statuszeile: aktiver Chip/Intent (Label oder null). */
  onActiveIntentChange?: (label: string | null) => void;
  /** Gespeicherter Modus dieses Chats (Id der bestehenden Modus-Liste). */
  hubModeId?: string | null;
  onHubModeCommit?: (modeId: string) => void;
  /** MEMCORE Modul B: Daumen-hoch unter Bot-Antworten (2-Schritt-Dialog im Parent). */
  enableMemorySave?: boolean;
  onMemorySaveRequest?: (args: { messageId: string; text: string }) => void;
  /** MEMCORE Modul C: Recall-Chip + Whitelist (Labor, Premium). */
  memcoreRecall?: (MemcoreContext & { curatorUrl: string }) | null;
  /**
   * Stream 4: Thread-gepinnte Fall-Eckdaten. Werden bei JEDER Anfrage automatisch
   * als memContext mitgesendet (Vorbefüllung späterer Entwürfe ohne Neu-Fragen).
   */
  pinnedMemContext?: MemProofItem[];
  /** Stream 4: „Merken“ unter einer Anwalts-Nachricht → Fakt im Thread pinnen. */
  onPinFact?: (text: string) => void;
  /** Cursor Agents UI — charcoal + grüner Akzent (Refero cursor.com/agents). */
  chatVariant?: 'default' | 'cursor';
  /** Hauptreiter: schwarzer Grund, lila Kante, keine weiße Karte. */
  appearance?: 'haupt';
  cursorCustomerName?: string;
  cursorKanzlei?: string;
  /** Nach Analyse-Upload: Dateien im Dokument-Hub registrieren und ggf. im Editor öffnen. */
  onAnalyzeDocumentsReady?: (fields: import('./portal/portalDocuments').PortalDocumentOpenFields[]) => void | Promise<void>;
  /** Phase 1.3: PB-Token für Akten-Anlage im geführten Flow. */
  authToken?: string | null;
  /** Phase 1.3: Initialer Flow-Zustand (persistiert pro Thread im Workspace). */
  guidedFlowState?: GuidedFlowState | null;
  onGuidedFlowStateChange?: (state: GuidedFlowState) => void;
  /** Phase 1.4: Absatz-Index aus Editor-Extract. */
  paragraphIndexMap?: ParagraphIndexEntry[];
  /** Phase 1.4: Fokus-Absatz für n8n `cursorSelection`. */
  cursorSelection?: CursorSelection | null;
  /** Phase 1.4: Nach Analyse Guard setzen (Thomas-Fix). */
  onMarkDocumentAnalyzed?: () => void;
}

/** Eindeutige Portal-Message-ID (bestehende Threads ohne id bleiben gültig). */
export function newMessageId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `msg_${crypto.randomUUID()}`;
  }
  return `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const InlineChat: React.FC<InlineChatProps> = ({
  title,
  subtitle,
  initialMessage,
  webhookUrl,
  accentColor = 'blue',
  mode = 'internal',
  extraBody = {},
  documentContext,
  documentContextEnabled = false,
  documentExtractFingerprint,
  documentAnalyzeDone = false,
  documentDisplayName,
  activeDocumentId,
  openDocumentsMeta,
  selectedDocuments,
  synthesisDocumentLabels,
  feedbackAkteBound = false,
  timeoutMs = 45000,
  showQuickActions = false,
  showRechtsfrageQuickAction = false,
  messages: messagesProp,
  onMessagesChange,
  usePortalChips = false,
  chipAccent = 'blue',
  onLoadingChange,
  headerAvatar,
  hideHeader = false,
  compactChrome = false,
  shellClassName = '',
  enableContractIntent = false,
  onContractGenerate,
  onExportSchreibenPdf,
  onExportSchreibenDocx,
  akteBound = false,
  akteHandoff = null,
  onContinueAkte,
  onOpenPortalDocument,
  onApplySynthesisToDocument,
  synthesisHasOpenDocument = false,
  customerId,
  onActiveIntentChange,
  hubModeId = null,
  onHubModeCommit,
  enableMemorySave = false,
  onMemorySaveRequest,
  memcoreRecall = null,
  pinnedMemContext,
  onPinFact,
  chatVariant = 'default',
  appearance,
  cursorCustomerName,
  cursorKanzlei,
  onAnalyzeDocumentsReady,
  authToken,
  guidedFlowState,
  onGuidedFlowStateChange,
  paragraphIndexMap,
  cursorSelection,
  onMarkDocumentAnalyzed,
}) => {
  const isControlled = messagesProp !== undefined && onMessagesChange !== undefined;
  const [internalMessages, setInternalMessages] = useState<Message[]>(() => [{ role: 'bot', text: initialMessage }]);
  const [copiedAnswerKey, setCopiedAnswerKey] = useState<string | null>(null);
  const [hauptTailPx, setHauptTailPx] = useState(0);
  const messages = isControlled ? messagesProp! : internalMessages;
  const demoSupportCtx = readPortalDemoSupportContext();
  const demoSupportEnabled = isPortalDemoSupportEnabled(demoSupportCtx);
  const demoSupportCustomerId = customerId || demoSupportCtx.customerId;
  const messageFeedbackUiDiag: FeedbackDiagMeta = {
    ui_view: compactChrome && activeDocumentId ? 'document_editor' : 'chat',
    pane: compactChrome && activeDocumentId ? 'document_split' : 'chat',
    tab: 'arko',
    doc_count: openDocumentsMeta?.length ?? 0,
    oo_open: Boolean(compactChrome && activeDocumentId),
    akte_bound: feedbackAkteBound,
  };

  const updateMessages = useCallback<MessagesUpdater>(
    (value) => {
      if (isControlled) {
        onMessagesChange!(value);
      } else {
        setInternalMessages(value);
      }
    },
    [isControlled, onMessagesChange],
  );

  // Spiegel der aktuellen Nachrichten — verhindert stale-closure beim Build des `history`-Bodys.
  const messagesRef = useRef<Message[]>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const [input, setInput]       = useState(() => {
    if (!customerId || typeof sessionStorage === 'undefined') return '';
    try {
      return sessionStorage.getItem(chatDraftKey(customerId)) ?? '';
    } catch {
      return '';
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [thinkingContext, setThinkingContext] = useState<ThinkingContext | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pendingIntent, setPendingIntent] = useState<QuickAction['intent'] | ''>('');
  /** Welcome-Chip „Fragen zum Dokument“ — Fokus + gezielte Antwort, kein Auto-Send. */
  const [pendingDocumentQuestion, setPendingDocumentQuestion] = useState(false);
  const sendAkteFuehrenRef = useRef<(text: string, intent?: ArkoRequestIntent) => void>(() => {});
  /** Modul C: Chip „Aus meinem Gedächtnis“ aktiv → Recall beim Senden. */
  const [pendingMemRecall, setPendingMemRecall] = useState(false);
  /** Stream 4: Message-IDs, die der Anwalt als Fall-Eckdaten gepinnt hat (UI-Feedback). */
  const [pinnedMsgIds, setPinnedMsgIds] = useState<Set<string>>(() => new Set());
  // Stream D: Slot-Filling-Zustand des Vertragsgenerators (in-memory pro Chat-Thread).
  const contractFlowRef = useRef<ContractFlowState>(EMPTY_CONTRACT_FLOW);
  const guidedFlowRef = useRef<GuidedFlowState>(guidedFlowState ?? EMPTY_GUIDED_FLOW);
  const guidedFlowUploadedFieldsRef = useRef<PortalDocumentOpenFields[]>([]);
  const [dismissedStructuredEditIds, setDismissedStructuredEditIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (guidedFlowState) guidedFlowRef.current = guidedFlowState;
  }, [guidedFlowState]);
  const syncGuidedFlowState = useCallback((next: GuidedFlowState) => {
    guidedFlowRef.current = next;
    onGuidedFlowStateChange?.(next);
  }, [onGuidedFlowStateChange]);
  // Stream 3: ID der Bot-Nachricht, die gerade als PDF/DOCX exportiert wird (Spinner).
  const [exportingPdfId, setExportingPdfId] = useState<string | null>(null);
  const [exportingDocxId, setExportingDocxId] = useState<string | null>(null);
  const [schreibenEditId, setSchreibenEditId] = useState<string | null>(null);
  const [schreibenEditText, setSchreibenEditText] = useState('');
  const [schreibenPlaceholderKind, setSchreibenPlaceholderKind] = useState<'pdf' | 'docx' | null>(null);
  const [mandatsEditId, setMandatsEditId] = useState<string | null>(null);
  const [mandatsEditDraft, setMandatsEditDraft] = useState<MandatsannahmeSlots>({});
  const mandatsEditDraftRef = useRef<MandatsannahmeSlots>({});
  const mandatsEditIdRef = useRef<string | null>(null);
  mandatsEditDraftRef.current = mandatsEditDraft;
  mandatsEditIdRef.current = mandatsEditId;
  const [applyingSynthesis, setApplyingSynthesis] = useState<{
    messageId: string;
    target: 'existing' | 'new';
  } | null>(null);
  const [synthesisRetryIds, setSynthesisRetryIds] = useState<Set<string>>(() => new Set());
  const pendingSynthesisReplyRef = useRef(false);
  const docxStandPrefixRef = useRef(false);
  const pendingWebArticleRef = useRef<{ article: WebArticleFetchResult; userIntent?: string } | null>(null);
  const scrollRef  = useRef<HTMLDivElement>(null);
  /** Klasse CHAT_SCROLL_FOLLOW: Follow nur am unteren Rand, nicht bei jedem Token. */
  const stickToBottomRef = useRef(true);
  const followRafRef = useRef<number | null>(null);
  const [contractAskOpen, setContractAskOpen] = useState(false);
  const lastPinnedUserMsgIdRef = useRef<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const chipBarRef = useRef<PortalChipBarHandle>(null);
  const composerRef = useRef<ArkoCursorComposerHandle>(null);
  /** Angeheftete §§ aus der Composer-Suchleiste — Kontext, kein Tray-Pin. */
  const [attachedParagraphs, setAttachedParagraphs] = useState<ParagraphRef[]>([]);
  const attachedParagraphsRef = useRef<ParagraphRef[]>([]);
  attachedParagraphsRef.current = attachedParagraphs;
  const [relatedParagraphs, setRelatedParagraphs] = useState<ParagraphRef[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const abortReasonRef = useRef<'user' | 'timeout' | null>(null);
  const pendingSendTextRef = useRef<string | null>(null);
  const skipLoadingGuardRef = useRef(false);
  const isLoadingRef = useRef(false);
  const oghJoinLexIdsRef = useRef<Set<string>>(new Set());
  const [oghJoinLexIds, setOghJoinLexIds] = useState<Set<string>>(() => new Set());
  const pendingAutoOghRef = useRef<{
    prompt: string;
    carry: LexCarry;
    lexMsgId: string;
    userText: string;
  } | null>(null);

  const beginLoading = useCallback((ctx: ThinkingContext) => {
    setThinkingContext(ctx);
    setIsLoading(true);
    isLoadingRef.current = true;
  }, []);

  const endLoading = useCallback(() => {
    setIsLoading(false);
    setThinkingContext(null);
    isLoadingRef.current = false;
  }, []);

  const isUserAborted = useCallback(() => abortReasonRef.current === 'user', []);

  const documentQuickActions: QuickAction[] = QUICK_ACTIONS;

  const isCursor = chatVariant === 'cursor';
  const hauptLook = appearance === 'haupt';
  const cursorBotBubble = CURSOR_CHAT.botBubble;
  const hauptUserBubble = 'w-full rounded-2xl border border-white/[0.06] bg-white/[0.045] text-slate-200';
  const hauptBotPlain = 'w-full border-0 bg-transparent text-slate-200 shadow-none';
  const quietBtn = hauptLook
    ? 'flex items-center gap-1.5 rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] text-violet-100 transition-colors hover:bg-violet-600/30 disabled:cursor-not-allowed disabled:opacity-50'
    : 'flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-slate-200 transition-colors hover:bg-white/[0.08] hover:text-white disabled:cursor-not-allowed disabled:opacity-50';
  const quietBtnIdle = hauptLook
    ? 'border-violet-400/40 bg-[#07070a] text-violet-100 hover:bg-violet-600/30'
    : 'border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08] hover:text-white';
  const effectiveParagraphMapForRender = React.useMemo(() => {
    if (paragraphIndexMap?.length) return paragraphIndexMap;
    if (documentContext?.trim()) return buildParagraphIndexMap(documentContext);
    return [];
  }, [paragraphIndexMap, documentContext]);
  const resolvedAccent = isCursor ? 'cursor' : accentColor;
  const accent = CHAT_ACCENT[resolvedAccent];
  const chatShell = isCursor ? CURSOR_CHAT : PORTAL_CHAT;
  const hasUserTurn = messages.some((m) => m.role === 'user' && m.text.trim());
  /** Cursor-Leerzustand nur ohne Thread-Inhalt — Bot-Hinweise (z. B. Hintergrundanalyse) müssen sichtbar bleiben. */
  const isCursorEmpty = isCursor && messages.length === 0;
  const resolvedChipAccent: UiAccent = isCursor ? 'cursor' : chipAccent;
  const arkoContextMeter = useMemo(() => {
    const lastLex = [...messages].reverse().find(
      (m) => m.role === 'bot' && (m.lexProof?.sources?.length ?? 0) > 0,
    );
    const lastWeb = [...messages].reverse().find(
      (m) => m.role === 'bot' && m.webProof?.card,
    );
    const extractTitles: string[] = [];
    if (documentContextEnabled) {
      for (const doc of selectedDocuments ?? []) {
        const title = String(doc.title || '').trim();
        if (title) extractTitles.push(title);
      }
      const fallback = documentDisplayName?.trim();
      if (!extractTitles.length && fallback) extractTitles.push(fallback);
    }
    return {
      messages,
      documentContext: documentContextEnabled ? (documentContext ?? null) : null,
      extractTitles,
      pendingIntent: pendingIntent || '',
      memItems: pinnedMemContext,
      lexSources: lastLex?.lexProof?.sources,
      akteHandoff,
      webCard: lastWeb?.webProof?.card ?? null,
    };
  }, [
    messages,
    documentContextEnabled,
    documentContext,
    selectedDocuments,
    documentDisplayName,
    pendingIntent,
    pinnedMemContext,
    akteHandoff,
  ]);

  useEffect(() => {
    if (messages.length === 0) {
      lastPinnedUserMsgIdRef.current = null;
      stickToBottomRef.current = true;
    } else {
      const last = messages[messages.length - 1];
      const pin = followAfterNewUserBubble(last?.role, last?.id, lastPinnedUserMsgIdRef.current);
      if (pin) {
        lastPinnedUserMsgIdRef.current = pin.pinnedId;
        stickToBottomRef.current = pin.follow;
      }
    }
    if (!shouldApplyStreamFollow(stickToBottomRef.current)) return;
    const id = requestAnimationFrame(() => {
      if (followRafRef.current === id) followRafRef.current = null;
      if (!shouldApplyStreamFollow(stickToBottomRef.current)) return;
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    followRafRef.current = id;
    return () => {
      cancelAnimationFrame(id);
      if (followRafRef.current === id) followRafRef.current = null;
    };
  }, [messages, isLoading, hauptTailPx]);

  useEffect(() => {
    if (!hauptLook) return;
    const el = scrollRef.current;
    if (!el) return;
    const apply = () => setHauptTailPx(Math.round(el.clientHeight * 0.25));
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [hauptLook, messages.length]);

  const detachChatFollow = useCallback(() => {
    stickToBottomRef.current = false;
    if (followRafRef.current != null) {
      cancelAnimationFrame(followRafRef.current);
      followRafRef.current = null;
    }
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => { adjustTextareaHeight(); }, [input, adjustTextareaHeight]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    onActiveIntentChange?.(arkoIntentLabel(pendingIntent));
  }, [pendingIntent, onActiveIntentChange]);

  useEffect(() => {
    const onArkoIntent = (e: Event) => {
      const detail = (e as CustomEvent<{ intent?: QuickAction['intent']; promptPrefix?: string; clear?: boolean }>).detail;
      if (detail?.clear) {
        setPendingIntent('');
        const prefix = detail.promptPrefix?.trim();
        if (prefix) setInput((prev) => (prev.trim() === prefix ? '' : prev));
        return;
      }
      if (!detail?.intent) return;
      setPendingIntent(detail.intent);
      if (detail.promptPrefix) {
        setInput((prev) => (prev.trim() ? prev : detail.promptPrefix ?? ''));
      }
      window.requestAnimationFrame(() => {
        textareaRef.current?.focus();
        document.querySelector<HTMLTextAreaElement>('[data-portal-arko-input]')?.focus();
      });
    };
    window.addEventListener(ARKO_INTENT_EVENT, onArkoIntent);
    return () => window.removeEventListener(ARKO_INTENT_EVENT, onArkoIntent);
  }, []);

  useEffect(() => {
    const onAkteFuehren = (e: Event) => {
      const detail = (e as CustomEvent<AkteFuehrenPrompt>).detail;
      const text = String(detail?.text || '').trimEnd();
      if (!text.trim()) return;
      if (detail.autoSend) {
        sendAkteFuehrenRef.current(text.trim(), detail.intent);
        return;
      }
      if (detail.intent === 'document_question') setPendingDocumentQuestion(true);
      setInput(text);
      window.requestAnimationFrame(() => {
        textareaRef.current?.focus();
        document.querySelector<HTMLTextAreaElement>('[data-portal-arko-input]')?.focus();
      });
    };
    window.addEventListener(ARKO_AKTE_FUEHREN_EVENT, onAkteFuehren);
    return () => window.removeEventListener(ARKO_AKTE_FUEHREN_EVENT, onAkteFuehren);
  }, []);

  useEffect(() => {
    const onPortalEscape = () => {
      if (pendingIntent) {
        setPendingIntent('');
        setInput(prev => stripQuickActionPrefix(prev));
      }
      if (pendingMemRecall) {
        setPendingMemRecall(false);
        setInput(prev => stripMemRecallPrefix(prev));
      }
      chipBarRef.current?.collapse();
      composerRef.current?.collapseTrays();
    };
    window.addEventListener('arkorbit:portal-escape', onPortalEscape);
    return () => window.removeEventListener('arkorbit:portal-escape', onPortalEscape);
  }, [pendingIntent, pendingMemRecall]);

  useEffect(() => {
    if (!customerId) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      try {
        const key = chatDraftKey(customerId);
        const trimmed = input.trim();
        if (trimmed) sessionStorage.setItem(key, input);
        else sessionStorage.removeItem(key);
      } catch { /* noop */ }
    }, CHAT_DRAFT_DEBOUNCE_MS);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [input, customerId]);

  const clearChatDraft = useCallback(() => {
    if (!customerId) return;
    try {
      sessionStorage.removeItem(chatDraftKey(customerId));
    } catch { /* noop */ }
  }, [customerId]);

  /**
   * Intent-Hygiene (Andreas 27.05.2026): Wenn der User die Rechtsfrage-Quick-
   * Action geklickt hat (pendingIntent='rechtsfrage'), das Inputfeld jetzt
   * aber NICHT mehr mit dem Chip-Prefix beginnt (weil er den Prefix-Text
   * geloescht und stattdessen z. B. „Kannst du das geoeffnete Dokument
   * zusammenfassen?“ tippt), darf das Backend nicht mehr den LEXCORE-Pfad
   * forcen. Wir loeschen den Pending-Intent entsprechend; das Backend hat
   * zusaetzlich einen Override fuer den Fall, dass der User den Prefix
   * doch sendet (Defense-in-Depth in `arko-portal-n8n-code.js` Section
   * INTENT_HEURISTIK).
   */
  useEffect(() => {
    if (pendingIntent !== 'rechtsfrage') return;
    const rechtsfrageHead = RECHTSFRAGE_ACTION.prefix.split('\n')[0].toLowerCase();
    const head = input.trimStart().toLowerCase();
    if (!head.startsWith(rechtsfrageHead) && attachedParagraphs.length === 0) {
      setPendingIntent('');
    }
  }, [input, pendingIntent, attachedParagraphs.length]);

  // Stream D — Vertragsgenerator-Slot-Filling. Verarbeitet einen Chat-Turn lokal
  // (kein Webhook), erfragt Pflichtfelder und ruft bei Vollständigkeit den
  // Generate-Endpoint via onContractGenerate.
  const runContractTurn = useCallback(async (text: string, isStart: boolean) => {
    updateMessages(prev => [...prev, mkMsg('user', text)]);
    const result = isStart
      ? startContractFlow(inferContractTemplateId(text) || CONTRACT_TEMPLATES[0]?.id || 'mietvertrag')
      : advanceContractFlow(contractFlowRef.current, text);
    contractFlowRef.current = result.state;
    if (result.botMessages.length) {
      updateMessages(prev => [...prev, ...result.botMessages.map(t => mkMsg('bot', t))]);
    }
    if (result.ready && onContractGenerate) {
      beginLoading({ flow: 'contract', chatInput: text });
      try {
        const gen = await onContractGenerate(result.ready);
        updateMessages(prev => [...prev, mkMsg('bot', gen.message)]);
      } catch {
        updateMessages(prev => [...prev, mkMsg(
          'bot',
          'Der Vertrags-Entwurf konnte nicht erstellt werden. Bitte versuchen Sie es später erneut.',
        )]);
      } finally {
        endLoading();
      }
      contractFlowRef.current = EMPTY_CONTRACT_FLOW;
    }
  }, [onContractGenerate, updateMessages, beginLoading, endLoading]);

  const appendGuidedBlocks = useCallback((
    blocks: GuidedFlowBotBlock[],
    extras?: {
      reasoningTrace?: ReasoningTrace | null;
      lexProof?: LexProof | null;
      judProof?: JudicatureProof | null;
      webProof?: WebSearchProof | null;
      structuredEdit?: StructuredDocumentEdit | null;
    },
  ) => {
    if (!blocks.length) return;
    updateMessages((prev) => [...prev, ...blocks.map((b) => mkGuidedFlowMsg(b, extras))]);
  }, [updateMessages]);

  const openGuidedIngestedDocument = useCallback(async () => {
    const fields = guidedFlowUploadedFieldsRef.current;
    if (!fields.length || !onAnalyzeDocumentsReady) return;
    await Promise.resolve(onAnalyzeDocumentsReady(fields));
  }, [onAnalyzeDocumentsReady]);

  const applyGuidedFlowSideEffects = useCallback(async (
    result: ReturnType<typeof advanceGuidedFlow>,
  ) => {
    if (result.markDocumentAnalyzed) {
      onMarkDocumentAnalyzed?.();
    }
    if (result.openIngestedDocument) {
      await openGuidedIngestedDocument();
    }
  }, [onMarkDocumentAnalyzed, openGuidedIngestedDocument]);

  const createAkteFromGuidedFlow = useCallback(async (
    input: Parameters<typeof createPortalAkteVertragBeratung>[2],
  ) => {
    const cid = customerId?.trim() ?? '';
    const token = authToken?.trim() ?? '';
    if (!cid || !token) {
      updateMessages((prev) => [
        ...prev,
        mkMsg('bot', 'Akte konnte nicht angelegt werden — bitte neu einloggen.'),
      ]);
      return;
    }
    const result = await createPortalAkteVertragBeratung(cid, token, input);
    if (result.ok === false) {
      updateMessages((prev) => [...prev, mkMsg('bot', result.error)]);
      return;
    }
    updateMessages((prev) => [
      ...prev,
      mkMsg(
        'bot',
        `Akte **${result.data.akte.title}** angelegt — Checkliste mit ${result.data.checklistCount} Punkten gestartet. ` +
          'Aktenwächter finden Sie im Dokumenten-Hub.',
      ),
    ]);
  }, [authToken, customerId, updateMessages]);

  const buildWebhookDocumentExtras = useCallback((
    docContext: string | null | undefined,
    selection?: CursorSelection | null,
  ): Record<string, unknown> => {
    const extras: Record<string, unknown> = {};
    const map =
      paragraphIndexMap?.length
        ? paragraphIndexMap
        : docContext?.trim()
          ? buildParagraphIndexMap(docContext)
          : [];
    if (map.length) {
      extras.paragraphIndexMap = map;
    }
    if (selection) {
      extras.cursorSelection = selection;
    } else if (cursorSelection) {
      extras.cursorSelection = cursorSelection;
    }
    return extras;
  }, [paragraphIndexMap, cursorSelection]);

  const runGuidedWebhook = useCallback(async (
    webhook: NonNullable<ReturnType<typeof advanceGuidedFlow>['webhook']>,
    state: GuidedFlowState,
  ) => {
    const historyAtSend = messagesRef.current;
    beginLoading({
      flow: 'webhook',
      chatInput: webhook.chatInput,
      intent: webhook.intent,
      hasDocumentContext: Boolean(webhook.documentContext),
    });
    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      abortReasonRef.current = null;
      const timeout = setTimeout(() => {
        abortReasonRef.current = 'timeout';
        controller.abort();
      }, timeoutMs);

      const requestId = newArkoRequestId();
      const docExtras = buildWebhookDocumentExtras(webhook.documentContext);
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
          ...portalAuthzHeaders(webhookUrl),
        },
        body: JSON.stringify({
          chatInput: webhook.chatInput,
          history: historyAtSend,
          mode,
          request_id: requestId,
          ...(webhook.intent ? { intent: webhook.intent } : {}),
          ...extraBody,
          ...(webhook.documentContext ? { documentContext: webhook.documentContext } : {}),
          ...docExtras,
          ...(documentExtractFingerprint ? { documentExtractFingerprint } : {}),
          ...(guidedFlowUploadedFieldsRef.current.length
            ? { documentAlreadyAnalyzed: true, skipProactiveFrist: true }
            : {}),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (isUserAborted()) return;

      if (!response.ok) {
        updateMessages((prev) => [
          ...prev,
          mkMsg('bot', `ARKO-Analyse fehlgeschlagen (HTTP ${response.status}). Bitte erneut versuchen.`),
        ]);
        return;
      }

      const data = await response.json();
      const raw = data?.data ?? data;
      const item = Array.isArray(raw) ? raw[0] : raw;
      const payload = item?.json ?? item ?? data;
      const reply =
        payload?.output ?? payload?.text ?? payload?.message ??
        payload?.message?.content ?? payload?.content ??
        (typeof payload === 'string' ? payload : null);
      const lexProof = parseLexProof(payload?.lexProof);
      const judProof = parseJudicatureProof(payload?.judProof);
      const webProof = parseWebSearchProof(payload?.webProof);
      const reasoningTrace = parseReasoningTrace(payload?.reasoning_trace);
      const structuredEdit = parseStructuredDocumentEdit(
        payload?.structured_edit ?? payload?.structuredEdit,
      );

      const relatedFromProof = relatedParagraphRefsFromLexProof(lexProof);
      if (relatedFromProof.length > 0) setRelatedParagraphs(relatedFromProof);

      const cont = continueGuidedFlowAfterWebhook(state, String(reply ?? ''));
      syncGuidedFlowState(cont.state);
      appendGuidedBlocks(cont.blocks, { reasoningTrace, lexProof, judProof, webProof, structuredEdit });
      await applyGuidedFlowSideEffects(cont);
      if (cont.createAkte) await createAkteFromGuidedFlow(cont.createAkte);
      if (cont.ended) syncGuidedFlowState(EMPTY_GUIDED_FLOW);
      if (cont.markDocumentAnalyzed) {
        onMarkDocumentAnalyzed?.();
      }
      if (cont.openIngestedDocument) {
        await openGuidedIngestedDocument();
      }
    } catch (err: unknown) {
      const isAbort = (err as { name?: string })?.name === 'AbortError';
      if (isAbort && isUserAborted()) return;
      updateMessages((prev) => [
        ...prev,
        mkMsg('bot', isAbort ? ARKO_TIMEOUT_USER_MESSAGE : ARKO_CONNECTIVITY_USER_MESSAGE),
      ]);
    } finally {
      abortControllerRef.current = null;
      abortReasonRef.current = null;
      endLoading();
    }
  }, [
    appendGuidedBlocks,
    applyGuidedFlowSideEffects,
    buildWebhookDocumentExtras,
    createAkteFromGuidedFlow,
    documentExtractFingerprint,
    endLoading,
    extraBody,
    beginLoading,
    isUserAborted,
    mode,
    onMarkDocumentAnalyzed,
    openGuidedIngestedDocument,
    syncGuidedFlowState,
    timeoutMs,
    updateMessages,
    webhookUrl,
  ]);

  const applyGuidedTurn = useCallback(async (
    result: ReturnType<typeof advanceGuidedFlow>,
  ) => {
    syncGuidedFlowState(result.state);
    appendGuidedBlocks(result.blocks);
    if (result.webhook) {
      await runGuidedWebhook(result.webhook, result.state);
      return;
    }
    await applyGuidedFlowSideEffects(result);
    if (result.createAkte) await createAkteFromGuidedFlow(result.createAkte);
    if (result.ended) syncGuidedFlowState(EMPTY_GUIDED_FLOW);
  }, [
    appendGuidedBlocks,
    applyGuidedFlowSideEffects,
    createAkteFromGuidedFlow,
    runGuidedWebhook,
    syncGuidedFlowState,
  ]);

  const commitMandatsConfirmEdit = useCallback((msg: Message, draftOverride?: MandatsannahmeSlots) => {
    const flow = guidedFlowRef.current;
    const base = msg.mandatsConfirm ?? {};
    const editingThis = Boolean(msg.id && mandatsEditIdRef.current === msg.id);
    const draft = draftOverride ?? (editingThis ? mandatsEditDraftRef.current : base);
    const nextSlots = applyMandatsConfirmEdits(base, draft);
    if (flow.active && flow.flowId === 'mandatsannahme') {
      syncGuidedFlowState({
        ...flow,
        data: {
          ...flow.data,
          mandant: nextSlots.mandant,
          gegenstand: nextSlots.gegenstand,
          dringlichkeit: nextSlots.dringlichkeit,
        },
      });
    }
    if (msg.id) {
      updateMessages((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, mandatsConfirm: nextSlots } : m)),
      );
    }
    setMandatsEditId(null);
    setMandatsEditDraft({});
    mandatsEditIdRef.current = null;
    mandatsEditDraftRef.current = {};
    return nextSlots;
  }, [syncGuidedFlowState, updateMessages]);

  const startMandatsConfirmEdit = useCallback((msg: Message) => {
    if (!msg.id || !msg.mandatsConfirm) return;
    setMandatsEditId(msg.id);
    mandatsEditIdRef.current = msg.id;
    const draft: MandatsannahmeSlots = {
      mandant: msg.mandatsConfirm.mandant ?? '',
      gegenstand: msg.mandatsConfirm.gegenstand ?? '',
      dringlichkeit: msg.mandatsConfirm.dringlichkeit ?? '',
    };
    setMandatsEditDraft(draft);
    mandatsEditDraftRef.current = draft;
  }, []);

  const cancelMandatsConfirmEdit = useCallback(() => {
    setMandatsEditId(null);
    setMandatsEditDraft({});
    mandatsEditIdRef.current = null;
    mandatsEditDraftRef.current = {};
  }, []);

  const updateMandatsConfirmDraft = useCallback((next: MandatsannahmeSlots) => {
    mandatsEditDraftRef.current = next;
    setMandatsEditDraft(next);
  }, []);

  const flushMandatsConfirmEdits = useCallback(() => {
    const editId = mandatsEditIdRef.current;
    if (!editId) return;
    const msg = messagesRef.current.find((m) => m.id === editId);
    if (msg?.mandatsConfirm) commitMandatsConfirmEdit(msg);
  }, [commitMandatsConfirmEdit]);

  const runGuidedFlowTurn = useCallback(async (
    text: string,
    options?: { documentContext?: string; fileNames?: string[] },
  ) => {
    flushMandatsConfirmEdits();
    updateMessages((prev) => [...prev, mkMsg('user', text)]);
    const result = advanceGuidedFlow(guidedFlowRef.current, text, options);
    await applyGuidedTurn(result);
  }, [applyGuidedTurn, flushMandatsConfirmEdits, updateMessages]);

  const handleGuidedFlowChip = useCallback((chip: GuidedFlowChip, msg?: Message) => {
    if (isLoading) return;
    if (chip.id === 'open_editor') {
      void (async () => {
        updateMessages((prev) => [...prev, mkMsg('user', chip.label)]);
        await openGuidedIngestedDocument();
        updateMessages((prev) => [
          ...prev,
          mkMsg('bot', 'Dokument im Editor geöffnet. Aktenwächter und weitere Prüfung im Dokumenten-Hub.'),
        ]);
        syncGuidedFlowState(EMPTY_GUIDED_FLOW);
      })();
      return;
    }
    const createAkteNow = /akte\s+jetzt/i.test(chip.label) || /akte\s+jetzt/i.test(chip.value);
    if (createAkteNow && msg?.mandatsConfirm) {
      commitMandatsConfirmEdit(msg);
    }
    void runGuidedFlowTurn(chip.value);
  }, [
    isLoading,
    openGuidedIngestedDocument,
    runGuidedFlowTurn,
    syncGuidedFlowState,
    updateMessages,
    commitMandatsConfirmEdit,
  ]);

  const cancelRequest = useCallback(() => {
    if (!isLoadingRef.current) return;
    abortReasonRef.current = 'user';
    abortControllerRef.current?.abort();
    skipLoadingGuardRef.current = false;
    const restore = pendingSendTextRef.current;
    pendingSendTextRef.current = null;
    if (restore) {
      setInput(restore);
      updateMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'user' && last.text === restore) {
          return prev.slice(0, -1);
        }
        return prev;
      });
      requestAnimationFrame(() => {
        adjustTextareaHeight();
        textareaRef.current?.focus();
      });
    }
    endLoading();
  }, [updateMessages, adjustTextareaHeight, endLoading]);

  const focusChatInput = useCallback(() => {
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      const cursorInput = document.querySelector<HTMLTextAreaElement>('[data-portal-arko-input]');
      cursorInput?.focus();
    });
  }, []);

  type SendMessageOptions = {
    documentContextOverride?: string;
    userDisplayText?: string;
    chipAction?: 'question' | 'variant_1' | 'variant_2' | 'variant_3' | 'ogh_dazu' | 'paragraph_lookup' | 'web_recherche';
    webArticleMeta?: { url: string; host: string; allowed: boolean; chars: number };
    skipWebArticleDetection?: boolean;
    lexCarry?: LexCarry | null;
    /** Auto-JUD: HTTP intern, keine User-Blase / Composer-Clear. Chip lässt das aus. */
    hideUserBubble?: boolean;
    /** Auto-Join: JUD-Ergebnis in die bestehende LEX-Blase mergen statt zweite ARKO-Blase. */
    mergeIntoBotMsgId?: string;
    /** Ursprüngliche Fallfrage für den Themenfilter (nicht der interne OGH-Prompt). */
    joinTopic?: string;
    /** Web-Gate: keine Akte / documentContext in die Such-API. */
    omitDocumentContext?: boolean;
  };

  const sendMessageRef = useRef<(
    text: string,
    intentOverride?: ArkoRequestIntent,
    opts?: SendMessageOptions,
  ) => Promise<void>>(async () => {});

  const markOghJoinForLexMsg = useCallback((lexMsgId: string) => {
    if (!lexMsgId || oghJoinLexIdsRef.current.has(lexMsgId)) return;
    oghJoinLexIdsRef.current.add(lexMsgId);
    setOghJoinLexIds(new Set(oghJoinLexIdsRef.current));
  }, []);

  const queueAutoOghJoin = useCallback((args: {
    lexProof?: LexProof | null;
    judProof?: JudicatureProof | null;
    schreibenDraft?: boolean;
    lexMsgId: string;
    userText: string;
    intent?: string;
    chipAction?: string;
  }) => {
    if (!args.lexMsgId || oghJoinLexIdsRef.current.has(args.lexMsgId) || pendingAutoOghRef.current) {
      return false;
    }
    const carry = sanitizeLexCarry(args.lexProof);
    const ids = lexCarryNormIds(carry);
    if (!shouldAutoOghJoin({
      isPremium: isPremiumPortalCaller(extraBody),
      intent: args.intent,
      chipAction: args.chipAction,
      userText: args.userText,
      lexVerified: Boolean(carry) && ids.length > 0,
      hasJudProof: Boolean(args.judProof),
      schreibenDraft: args.schreibenDraft,
      alreadyJoined: false,
    })) {
      return false;
    }
    const prompt = buildOghFollowupPrompt(
      stripQuickActionPrefix(args.userText),
      ids,
    );
    if (!prompt || !carry) return false;
    markOghJoinForLexMsg(args.lexMsgId);
    pendingAutoOghRef.current = {
      prompt,
      carry,
      lexMsgId: args.lexMsgId,
      userText: stripQuickActionPrefix(args.userText),
    };
    return true;
  }, [extraBody, markOghJoinForLexMsg]);

  const applyJoinVoiceToBotMsg = useCallback((
    lexMsgId: string,
    judProof?: JudicatureProof | null,
    joinTopic?: string,
  ) => {
    if (!lexMsgId) return;
    updateMessages((prev) =>
      prev.map((m) => {
        if (m.id !== lexMsgId) return m;
        const topicProof = applyJoinTopicToJudProof(judProof, {
          userQuestion: joinTopic,
          lexSources: m.lexProof?.sources,
        });
        return {
          ...m,
          text: buildLexJudJoinSynthesis({
            lexEinordnung: m.text,
            lexProof: m.lexProof,
            judProof: topicProof,
            userQuestion: joinTopic,
          }),
          ...(topicProof ? { judProof: topicProof } : {}),
          lexJudJoinVoice: true,
          streamDraft: false,
          streamBadge: null,
        };
      }),
    );
    markOghJoinForLexMsg(lexMsgId);
  }, [updateMessages, markOghJoinForLexMsg]);

  const sendMessage = useCallback(async (
    text: string,
    intentOverride?: ArkoRequestIntent,
    opts?: SendMessageOptions,
  ) => {
    if (!text.trim() || (isLoading && !skipLoadingGuardRef.current)) return;
    skipLoadingGuardRef.current = false;
    const hideUserBubble = Boolean(opts?.hideUserBubble);

    const pinnedRefs = hideUserBubble ? [] : attachedParagraphsRef.current;
    const paragraphPrefix =
      pinnedRefs.length > 0 && !text.includes(ATTACHED_PARAGRAPH_CONTEXT_HEAD)
        ? buildAttachedParagraphContext(pinnedRefs)
        : '';
    text = paragraphPrefix + text;
    if (pinnedRefs.length > 0 && !intentOverride) {
      intentOverride = 'rechtsfrage';
    }

    pendingSendTextRef.current = hideUserBubble ? null : text;
    if (!hideUserBubble) {
      setInput('');
      clearChatDraft();
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }

    if (!hideUserBubble && !opts?.skipWebArticleDetection && !opts?.documentContextOverride && ARKO_WEB_ARTICLE_FETCH_URL) {
      const webReq = detectWebArticleRequest(text);
      if (webReq) {
        updateMessages((prev) => [...prev, mkMsg('user', text)]);
        beginLoading({ flow: 'web_article_fetch', chatInput: text });
        setPendingIntent('');
        setPendingMemRecall(false);
        try {
          const article = await fetchWebArticle(webReq.url);
          if (!article.ok) {
            updateMessages((prev) => [
              ...prev,
              mkMsg('bot', article.error || 'Artikel konnte nicht geladen werden.'),
            ]);
            return;
          }
          pendingWebArticleRef.current = { article, userIntent: webReq.userIntent };
          updateMessages((prev) => [
            ...prev,
            {
              role: 'bot',
              text: formatWebArticlePreview(article),
              id: newMessageId(),
              messageKind: 'web_article',
              webArticle: article,
              webArticleUserIntent: webReq.userIntent,
            },
          ]);
        } finally {
          endLoading();
          pendingSendTextRef.current = null;
        }
        return;
      }
    }

    let intentForRequest = intentOverride ?? pendingIntent;
    let chipAction = opts?.chipAction;

    if (!hideUserBubble && !intentOverride && pendingDocumentQuestion) {
      const resolved = resolveDocumentQuestionSendIntent(text);
      intentForRequest = resolved.intent;
      chipAction = resolved.chipAction;
      if (intentForRequest === 'document_question' && looksLikeLegalQuestion(text)) {
        intentForRequest = 'rechtsfrage';
        chipAction = undefined;
      }
      setPendingDocumentQuestion(false);
    }
    const memRecallChip = pendingMemRecall;
    const memRecallTriggered = Boolean(
      !hideUserBubble && memcoreRecall && shouldMemcoreRecall(text, memRecallChip),
    );
    const memRecallTrigger: 'chip' | 'whitelist' = memRecallChip ? 'chip' : 'whitelist';

    // Phase 1.3 — Geführter Kachel-Flow (lokal, ggf. Webhook in applyGuidedTurn).
    if (!hideUserBubble && guidedFlowRef.current.active) {
      setPendingIntent('');
      await runGuidedFlowTurn(text);
      pendingSendTextRef.current = null;
      return;
    }

    // Stream D — Vertragsgenerator: aktiver Flow ODER Erstellungswunsch (kein Webhook).
    // Rechtsfrage-Chip und Chip Schreiben (intent=schreiben_erstellen) haben Vorrang.
    if (!hideUserBubble && enableContractIntent && onContractGenerate) {
      if (contractFlowRef.current.active) {
        setPendingIntent('');
        await runContractTurn(text, false);
        return;
      }
      if (
        intentForRequest !== 'rechtsfrage' &&
        intentForRequest !== 'schreiben_erstellen' &&
        looksLikeContractRequest(text)
      ) {
        setPendingIntent('');
        await runContractTurn(text, true);
        return;
      }
    }

    const historyAtSend = messagesRef.current;
    const userBubbleText = opts?.userDisplayText?.trim() || text;
    const docIdForWelcome = activeDocumentId?.trim() || null;
    const showDocWelcome =
      !hideUserBubble &&
      documentContextEnabled &&
      documentContext &&
      docIdForWelcome &&
      !hasDocumentWelcomeBeenShown(customerId ?? '', docIdForWelcome) &&
      !intentForRequest &&
      isDocumentWelcomeGreeting(text);

    if (showDocWelcome) {
      markDocumentWelcomeShown(customerId ?? '', docIdForWelcome);
      updateMessages((prev) => [
        ...prev,
        mkMsg('user', userBubbleText),
        {
          role: 'bot',
          text: buildDocumentWelcomeText(documentDisplayName ?? 'Dokument'),
          messageKind: 'document_welcome',
          documentWelcomeActions: buildDocumentWelcomeActions(),
          id: newMessageId(),
        },
      ]);
      setPendingIntent('');
      setPendingMemRecall(false);
      endLoading();
      pendingSendTextRef.current = null;
      return;
    }

    // Absatz-Ref VOR User-Bubble berechnen — sonst TDZ (const später im Scope).
    const attachmentContext =
      opts?.documentContextOverride != null && opts.documentContextOverride.trim() !== ''
        ? opts.documentContextOverride.trim()
        : null;

    const includeDocumentContext =
      !opts?.omitDocumentContext &&
      intentOverride !== 'web_recherche' &&
      (
        attachmentContext != null ||
        (
          documentContextEnabled &&
          documentContext != null &&
          documentContext !== '' &&
          !contractFlowRef.current.active
        )
      );

    const documentContextForRequest =
      attachmentContext ?? (includeDocumentContext ? documentContext : null);
    const includeBoundDocumentMetadata =
      documentContextEnabled && !contractFlowRef.current.active;
    const akteTurnContextPayload = buildAkteTurnContextPayload(
      documentContextForRequest,
      includeBoundDocumentMetadata ? selectedDocuments : undefined,
    );
    const selectedDocumentsForRequest = akteTurnContextPayload.selectedDocuments;

    const effectiveParagraphMap =
      paragraphIndexMap?.length
        ? paragraphIndexMap
        : documentContextForRequest?.trim()
          ? buildParagraphIndexMap(documentContextForRequest)
          : [];
    const paragraphRef = resolveParagraphReference(text, effectiveParagraphMap);
    const selectionForRequest = paragraphRef
      ? paragraphEntryToCursorSelection(paragraphRef, 'user_reference')
      : cursorSelection ?? null;

    if (!hideUserBubble) {
      updateMessages(prev => [...prev, mkMsg('user', userBubbleText, undefined, undefined, undefined, undefined, undefined, undefined, undefined, paragraphRef)]);
      setPendingIntent('');
      setPendingMemRecall(false);
    }

    // Sichtbare Antwort: alle aktiven Einträge dieses Sitzes plus Pins.
    // Recall (Notizblock/Whitelist) hängt zusätzlich an. Hintergrund bleibt ohne Kontext.
    let seatMemRows: MemVermerk[] = [];
    if (!hideUserBubble && memcoreRecall) {
      try {
        const token = getPortalPbToken();
        if (token) {
          const loaded = await listMeineVermerke(
            getPortalPocketBaseUrl(),
            token,
            memcoreRecall.userId,
          );
          const seat = memcoreRecall.userId;
          seatMemRows = loaded.map((row) => ({
            ...row,
            user_id: String(row.user_id || seat).trim(),
          }));
        }
      } catch {
        seatMemRows = [];
      }
    }
    let recallHits: MemProofItem[] | undefined;
    if (memRecallTriggered && memcoreRecall) {
      const recall = await runMemcoreRecall(
        memcoreRecall.curatorUrl,
        { kanzleiId: memcoreRecall.kanzleiId, userId: memcoreRecall.userId },
        text,
        memRecallTrigger,
      );
      if (recall.status === 'ok' && recall.vermerke && recall.vermerke.length > 0) {
        recallHits = recall.vermerke.map((v) => ({
          vermerk_id: v.vermerk_id,
          inhalt: v.inhalt,
          gueltig_von: v.gueltig_von,
          vertrauen: v.vertrauen,
        }));
      }
    }
    const sentMemContext = visibleReplyMemContext({
      visible: !hideUserBubble,
      rows: seatMemRows,
      userId: memcoreRecall?.userId ?? '',
      pinned: pinnedMemContext,
      recall: recallHits,
    });
    const memContext = sentMemContext.length > 0 ? sentMemContext : undefined;
    // Nachweis-Pfeil: nur wenn der Anwalt aktiv abgefragt hat (Recall), nicht bei
    // jeder still vorbefüllten Antwort — hält die Antwort-UI ruhig.
    const memProofForReply = memRecallTriggered ? memContext : undefined;

    const skipProactiveFrist =
      documentAnalyzeDone && !isExplicitPortalFristQuestion(text);

    const isSynthesisRequest = (selectedDocumentsForRequest?.length ?? 0) >= 2;
    if (!hideUserBubble && isSynthesisRequest) {
      pendingSynthesisReplyRef.current = true;
    }

    const explicitSingleTask =
      chipAction === 'web_recherche'
      || chipAction === 'schreiben_erstellen'
      || intentForRequest === 'web_recherche'
      || intentForRequest === 'schreiben_erstellen';
    const taskSplit = explicitSingleTask
      ? { requestText: text, deferred: null }
      : splitPortalTasks(text);
    const outboundText = taskSplit.requestText || text;

    beginLoading({
      flow: 'webhook',
      chatInput: hideUserBubble ? '' : outboundText,
      intent: intentForRequest || undefined,
      hasDocumentContext: Boolean(documentContextForRequest),
      hasWebArticleMeta: Boolean(opts?.webArticleMeta),
      skipProactiveFrist: skipProactiveFrist,
    });

    try {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      abortReasonRef.current = null;
      const timeout = setTimeout(() => {
        abortReasonRef.current = 'timeout';
        controller.abort();
      }, timeoutMs);

      const requestId = newArkoRequestId();
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Id': requestId,
          ...portalAuthzHeaders(webhookUrl),
        },
        body: JSON.stringify({
          chatInput: outboundText,
          history: historyAtSend,
          mode,
          request_id: requestId,
          stream: true,
          preferStream: true,
          ...(intentForRequest ? { intent: intentForRequest } : {}),
          ...(intentForRequest === 'judicature_recherche' ? { judicatureMode: true } : {}),
          ...(intentForRequest === 'web_recherche' ? { webSearchMode: true } : {}),
          ...(chipAction ? { chip_action: chipAction } : {}),
          ...(opts?.lexCarry ? { lexCarry: opts.lexCarry } : {}),
          ...extraBody,
          ...akteTurnContextPayload,
          ...(includeBoundDocumentMetadata && activeDocumentId?.trim()
            ? { activeDocumentId: activeDocumentId.trim() }
            : {}),
          ...(includeBoundDocumentMetadata && openDocumentsMeta?.length
            ? { openDocuments: openDocumentsMeta }
            : {}),
          ...(documentExtractFingerprint
            ? { documentExtractFingerprint }
            : {}),
          ...(skipProactiveFrist
            ? { skipProactiveFrist: true, documentAlreadyAnalyzed: true }
            : {}),
          ...(opts?.webArticleMeta ? { webArticleMeta: opts.webArticleMeta } : {}),
          ...(memContext && memContext.length > 0 ? { memContext } : {}),
          ...buildWebhookDocumentExtras(documentContextForRequest, selectionForRequest),
        }),
        signal: controller.signal,
      });
      clearTimeout(timeout);
      if (isUserAborted()) return;

      if (!response.ok) {
        const status = response.status;
        reportArkoHttpError(webhookUrl, status);
        let friendly: string;
        if (status === 404) {
          friendly = 'Der ARKO-Workflow ist auf dem Server noch nicht aktiviert. Bitte ARKORBIT informieren.';
        } else if (status === 429) {
          friendly = 'Zu viele Anfragen in kurzer Zeit. Bitte 30 Sekunden warten und erneut versuchen.';
        } else if (status >= 500) {
          friendly = `ARKO konnte gerade nicht antworten (Fehler ${status}). Bitte erneut versuchen. Bleibt das Problem, ARKORBIT informieren.`;
        } else {
          friendly = `ARKO konnte die Anfrage nicht annehmen (Fehler ${status}). Bitte Inhalt prüfen oder erneut versuchen.`;
        }
        if (opts?.mergeIntoBotMsgId) {
          applyJoinVoiceToBotMsg(opts.mergeIntoBotMsgId, null, opts.joinTopic);
        } else {
          updateMessages(prev => [...prev, mkMsg('bot', friendly)]);
        }
        pendingSynthesisReplyRef.current = false;
        return;
      }

      const data = await response.json();
      if (isUserAborted()) return;
      const raw     = data?.data ?? data;
      const item    = Array.isArray(raw) ? raw[0] : raw;
      const payload = item?.json ?? item ?? data;

      // Streaming-Handoff: Tokens vom Bridge, Gate ersetzt Draft durch Final.
      const streamMeta = payload?.stream;
      if (payload?.mode === 'stream' && streamMeta?.path) {
        const streamMsgId = newMessageId();
        const streamPath = String(streamMeta.path);
        const streamUrl = streamPath.startsWith('http')
          ? streamPath
          : `${window.location.origin}${streamPath.startsWith('/') ? '' : '/'}${streamPath}`;
        const earlyLexProof = parseLexProof(payload?.lexProof);
        const earlyCarry = sanitizeLexCarry(earlyLexProof);
        const earlyVoice = shouldAutoOghJoin({
          isPremium: isPremiumPortalCaller(extraBody),
          intent: intentForRequest,
          chipAction,
          userText: text,
          lexVerified: Boolean(earlyCarry) && lexCarryNormIds(earlyCarry).length > 0,
          hasJudProof: false,
        });
        updateMessages((prev) => [
          ...prev,
          {
            role: 'bot',
            text: '',
            id: streamMsgId,
            streamDraft: true,
            streamBadge: null,
            ...(earlyLexProof ? { lexProof: earlyLexProof } : {}),
            ...(earlyVoice ? { lexJudJoinVoice: true } : {}),
          },
        ]);
        let draftText = '';
        try {
          await consumeArkoSse(
            streamUrl,
            portalAuthzHeaders(streamUrl),
            controller.signal,
            {
              onMeta: (meta) => {
                const badge = typeof meta.badge === 'string' ? meta.badge : null;
                if (badge) {
                  updateMessages((prev) =>
                    prev.map((m) => (m.id === streamMsgId ? { ...m, streamBadge: badge } : m)),
                  );
                }
              },
              onLexSources: (data) => {
                const lexProof = parseLexProof(data);
                if (!lexProof) return;
                const carry = sanitizeLexCarry(lexProof);
                const joinVoice = shouldAutoOghJoin({
                  isPremium: isPremiumPortalCaller(extraBody),
                  intent: intentForRequest,
                  chipAction,
                  userText: text,
                  lexVerified: Boolean(carry) && lexCarryNormIds(carry).length > 0,
                  hasJudProof: false,
                });
                updateMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamMsgId
                      ? { ...m, lexProof, ...(joinVoice ? { lexJudJoinVoice: true } : {}) }
                      : m,
                  ),
                );
              },
              onToken: (piece) => {
                draftText += piece;
                const snap = draftText;
                updateMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamMsgId ? { ...m, text: snap, streamDraft: true } : m,
                  ),
                );
              },
              onStatus: (st) => {
                if (st.phase === 'gate' || st.badge) {
                  updateMessages((prev) =>
                    prev.map((m) =>
                      m.id === streamMsgId
                        ? { ...m, streamBadge: String(st.badge || 'wird geprüft') }
                        : m,
                    ),
                  );
                }
              },
              onFinal: (finalPayload) => {
                const fp = (finalPayload && typeof finalPayload === 'object'
                  ? finalPayload
                  : {}) as Record<string, unknown>;
                const finalReplyRaw =
                  (fp.output as string) ??
                  (fp.text as string) ??
                  draftText ??
                  'Keine Antwort erhalten.';
                const finalReply = dropDeferredAnswerSection(
                  String(finalReplyRaw),
                  Boolean(taskSplit.deferred),
                );
                const lexProof = parseLexProof(fp.lexProof);
                const judProof = parseJudicatureProof(fp.judProof);
                const webProof = parseWebSearchProof(fp.webProof);
                reportArkoQualityGateReject(webhookUrl, lexProof);
                const schreibenDraft = fp.schreibenDraft === true;
                const suggestedActions = mergeDeferredChip(
                  parseSuggestedActions(fp.suggestedActions ?? fp.actionHints),
                  taskSplit.deferred,
                );
                const approvalFrist = parseApprovalFristFromPayload(fp);
                const reasoningTrace = parseReasoningTrace(fp.reasoning_trace);
                const structuredEdit = parseStructuredDocumentEdit(
                  fp.structured_edit ?? fp.structuredEdit,
                );
                const relatedFromProof = relatedParagraphRefsFromLexProof(lexProof);
                if (relatedFromProof.length > 0) setRelatedParagraphs(relatedFromProof);
                const willJoin = queueAutoOghJoin({
                  lexProof,
                  judProof,
                  schreibenDraft,
                  lexMsgId: streamMsgId,
                  userText: text,
                  intent: intentForRequest,
                  chipAction,
                });
                updateMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamMsgId
                      ? {
                          ...mkMsg(
                            'bot',
                            String(finalReply),
                            memProofForReply,
                            lexProof,
                            schreibenDraft,
                            suggestedActions,
                            approvalFrist,
                            reasoningTrace,
                            pendingSynthesisReplyRef.current,
                            null,
                            structuredEdit,
                            judProof,
                            webProof,
                          ),
                          id: streamMsgId,
                          streamDraft: false,
                          streamBadge: null,
                          ...(willJoin || m.lexJudJoinVoice ? { lexJudJoinVoice: true } : {}),
                        }
                      : m,
                  ),
                );
                pendingSynthesisReplyRef.current = false;
              },
              onError: () => {
                updateMessages((prev) =>
                  prev.map((m) =>
                    m.id === streamMsgId
                      ? {
                          ...m,
                          text: m.text?.trim()
                            ? m.text
                            : ARKO_CONNECTIVITY_USER_MESSAGE,
                          streamDraft: false,
                          streamBadge: null,
                        }
                      : m,
                  ),
                );
              },
            },
          );
        } catch {
          updateMessages((prev) =>
            prev.map((m) =>
              m.id === streamMsgId
                ? {
                    ...m,
                    text: m.text?.trim() ? m.text : ARKO_CONNECTIVITY_USER_MESSAGE,
                    streamDraft: false,
                    streamBadge: null,
                  }
                : m,
            ),
          );
        }
        if (documentContextForRequest && (attachmentContext || includeDocumentContext)) {
          onMarkDocumentAnalyzed?.();
        }
        return;
      }

      const replyRaw =
        payload?.output ?? payload?.text ?? payload?.message ??
        payload?.message?.content ?? payload?.content ??
        (typeof payload === 'string' ? payload : null);
      const reply = replyRaw == null
        ? null
        : dropDeferredAnswerSection(String(replyRaw), Boolean(taskSplit.deferred));
      const lexProof = parseLexProof(payload?.lexProof);
      const judProof = parseJudicatureProof(payload?.judProof);
      const webProof = parseWebSearchProof(payload?.webProof);
      reportArkoQualityGateReject(webhookUrl, lexProof);
      const schreibenDraft = payload?.schreibenDraft === true;
      // Stream 5: Backend nennt passende Quick-Actions (Feldname suggestedActions,
      // actionHints als Alias). Frontend validiert + rendert die Chip-Leiste.
      const suggestedActions = mergeDeferredChip(
        parseSuggestedActions(payload?.suggestedActions ?? payload?.actionHints),
        taskSplit.deferred,
      );
      const approvalFrist = parseApprovalFristFromPayload(payload);
      const reasoningTrace = parseReasoningTrace(payload?.reasoning_trace);
      const synthesisDraft = pendingSynthesisReplyRef.current;
      const structuredEdit = parseStructuredDocumentEdit(
        payload?.structured_edit ?? payload?.structuredEdit,
      );
      pendingSynthesisReplyRef.current = false;

      const relatedFromProof = relatedParagraphRefsFromLexProof(lexProof);
      if (relatedFromProof.length > 0) setRelatedParagraphs(relatedFromProof);

      if (documentContextForRequest && (attachmentContext || includeDocumentContext)) {
        onMarkDocumentAnalyzed?.();
      }

      if (opts?.mergeIntoBotMsgId) {
        applyJoinVoiceToBotMsg(opts.mergeIntoBotMsgId, judProof, opts.joinTopic);
      } else {
        const botMsg = mkMsg(
          'bot',
          reply ?? JSON.stringify(data),
          memProofForReply,
          lexProof,
          schreibenDraft,
          suggestedActions,
          approvalFrist,
          reasoningTrace,
          synthesisDraft,
          null,
          structuredEdit,
          judProof,
          webProof,
        );
        const willJoin = queueAutoOghJoin({
          lexProof,
          judProof,
          schreibenDraft,
          lexMsgId: botMsg.id || '',
          userText: text,
          intent: intentForRequest,
          chipAction,
        });
        if (willJoin) botMsg.lexJudJoinVoice = true;
        updateMessages(prev => [...prev, botMsg]);
      }
    } catch (err: unknown) {
      pendingSynthesisReplyRef.current = false;
      const isAbort = (err as { name?: string })?.name === 'AbortError';
      if (isAbort && isUserAborted()) {
        return;
      }
      if (opts?.mergeIntoBotMsgId) {
        applyJoinVoiceToBotMsg(opts.mergeIntoBotMsgId, null, opts.joinTopic);
      } else {
        updateMessages(prev => [...prev, mkMsg(
          'bot',
          isAbort ? ARKO_TIMEOUT_USER_MESSAGE : ARKO_CONNECTIVITY_USER_MESSAGE,
        )]);
      }
    } finally {
      if (!isUserAborted()) {
        pendingSendTextRef.current = null;
      }
      abortReasonRef.current = null;
      abortControllerRef.current = null;
      const pendingJoin = pendingAutoOghRef.current;
      pendingAutoOghRef.current = null;
      if (pendingJoin && !isUserAborted()) {
        skipLoadingGuardRef.current = true;
        void sendMessageRef.current(pendingJoin.prompt, 'judicature_recherche', {
          chipAction: 'ogh_dazu',
          lexCarry: pendingJoin.carry,
          hideUserBubble: !shouldShowOghFollowupUserBubble('auto'),
          mergeIntoBotMsgId: pendingJoin.lexMsgId,
          joinTopic: pendingJoin.userText,
          skipWebArticleDetection: true,
        });
      } else {
        endLoading();
      }
    }
  }, [isLoading, mode, webhookUrl, timeoutMs, extraBody, documentContext, documentContextEnabled, documentDisplayName, activeDocumentId, openDocumentsMeta, selectedDocuments, documentExtractFingerprint, documentAnalyzeDone, pendingIntent, pendingDocumentQuestion, pendingMemRecall, memcoreRecall, pinnedMemContext, updateMessages, enableContractIntent, onContractGenerate, runContractTurn, runGuidedFlowTurn, clearChatDraft, customerId, beginLoading, endLoading, isUserAborted, paragraphIndexMap, cursorSelection, buildWebhookDocumentExtras, onMarkDocumentAnalyzed, queueAutoOghJoin, applyJoinVoiceToBotMsg]);
  sendMessageRef.current = sendMessage;
  sendAkteFuehrenRef.current = (text, intent) => {
    void sendMessage(text, intent);
  };

  const resolvePortalCustomerId = useCallback((): string | null => {
    const fromExtra = extraBody.customerId;
    if (typeof fromExtra === 'string' && fromExtra.trim()) return fromExtra.trim();
    if (customerId?.trim()) return customerId.trim();
    return null;
  }, [extraBody, customerId]);

  const resolvePortalTenantExtras = useCallback((): Record<string, string> => {
    const tenant = extraBody.tenant;
    if (typeof tenant === 'string' && tenant.trim()) {
      return { tenant: tenant.trim() };
    }
    return {};
  }, [extraBody]);

  const handleParagraphLookup = useCallback(
    (_refs: ParagraphRef[], prompt: string) => {
      if (isLoading || !prompt.trim()) return;
      void sendMessage(RECHTSFRAGE_ACTION.prefix + prompt, 'rechtsfrage', {
        chipAction: 'paragraph_lookup',
      });
    },
    [isLoading, sendMessage],
  );

  const handleJudicatureLookup = useCallback(
    (query: string) => {
      if (isLoading) return;
      const req = judSearchLookupRequest(query);
      if (!req) return;
      // Unabhängige Anwaltssuche — bewusst ohne lexCarry (kein Fall-Join).
      void sendMessage(req.chatInput, req.intent);
    },
    [isLoading, sendMessage],
  );

  const handleWebSearchLookup = useCallback(
    (query: string) => {
      if (isLoading) return;
      const req = webSearchLookupRequest(query);
      if (!req) return;
      void sendMessage(req.chatInput, req.intent, {
        chipAction: 'web_recherche',
        omitDocumentContext: true,
      });
    },
    [isLoading, sendMessage],
  );

  const handleAttachedParagraphsChange = useCallback((next: ParagraphRef[]) => {
    setAttachedParagraphs(next);
    if (next.length > 0) setPendingIntent('rechtsfrage');
  }, []);

  const handleComposerSubmit = useCallback(async (
    text: string,
    mode?: ArkoHubMode,
    attachmentFiles?: File[],
  ) => {
    const files = attachmentFiles?.filter(Boolean) ?? [];
    if (!files.length) {
      void sendMessage(text, mode?.intent);
      return;
    }

    const cid = resolvePortalCustomerId();
    if (!cid) {
      updateMessages((prev) => [
        ...prev,
        mkMsg('bot', 'Dokument-Analyse: Kunden-ID fehlt — bitte Seite neu laden.'),
      ]);
      return;
    }

    pendingSendTextRef.current = text;
    beginLoading({ flow: 'document_analyze', chatInput: text });
    const controller = new AbortController();
    abortControllerRef.current = controller;
    abortReasonRef.current = null;

    try {
      const { documentContext: attachCtx, fileNames, uploadedFields } =
        await prepareChatAnalyzeDocumentContext({
        files,
        customerId: cid,
        tenantExtras: resolvePortalTenantExtras(),
        kanzlei: typeof extraBody.kanzlei === 'string' ? extraBody.kanzlei : undefined,
        email: typeof extraBody.email === 'string' ? extraBody.email : undefined,
        signal: controller.signal,
      });

      if (isUserAborted()) return;

      if (uploadedFields.length && onAnalyzeDocumentsReady) {
        guidedFlowUploadedFieldsRef.current = uploadedFields;
        await Promise.resolve(onAnalyzeDocumentsReady(uploadedFields));
      }

      const chatInput = buildAnalyzeChatInput(fileNames, text);
      const displayText = buildAnalyzeUserDisplayText(fileNames, text);

      if (guidedFlowRef.current.active) {
        skipLoadingGuardRef.current = true;
        endLoading();
        pendingSendTextRef.current = null;
        await runGuidedFlowTurn(displayText || chatInput, {
          documentContext: attachCtx,
          fileNames,
        });
        return;
      }

      skipLoadingGuardRef.current = true;
      await sendMessage(chatInput, mode?.intent ?? 'rechtsfrage', {
        documentContextOverride: attachCtx,
        userDisplayText: displayText,
      });
    } catch (err: unknown) {
      if ((err as { name?: string })?.name === 'AbortError' && isUserAborted()) {
        return;
      }
      pendingSendTextRef.current = null;
      const restore = text;
      if (restore) setInput(restore);
      updateMessages((prev) => [
        ...prev,
        mkMsg(
          'bot',
          err instanceof Error
            ? err.message
            : 'Die Dokumente konnten nicht hochgeladen oder extrahiert werden.',
        ),
      ]);
      endLoading();
    } finally {
      abortControllerRef.current = null;
      if (!isUserAborted()) {
        pendingSendTextRef.current = null;
      }
    }
  }, [
    sendMessage,
    resolvePortalCustomerId,
    resolvePortalTenantExtras,
    updateMessages,
    beginLoading,
    endLoading,
    isUserAborted,
    onAnalyzeDocumentsReady,
    runGuidedFlowTurn,
  ]);

  const schreibenSourceText = useCallback((msg: Message): string => {
    if (msg.id && schreibenEditId === msg.id) return schreibenEditText;
    return msg.text;
  }, [schreibenEditId, schreibenEditText]);

  const commitSchreibenEdit = useCallback((msg: Message): string => {
    const text = schreibenSourceText(msg);
    if (msg.id && text !== msg.text) {
      updateMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, text } : m)));
    }
    return text;
  }, [schreibenSourceText, updateMessages]);

  const startSchreibenEdit = useCallback((msg: Message) => {
    if (!msg.id) return;
    setSchreibenEditId(msg.id);
    setSchreibenEditText(msg.text);
    setSchreibenPlaceholderKind(null);
  }, []);

  const finishSchreibenEdit = useCallback((msg: Message) => {
    commitSchreibenEdit(msg);
    setSchreibenEditId(null);
    setSchreibenPlaceholderKind(null);
  }, [commitSchreibenEdit]);

  const cancelSchreibenEdit = useCallback(() => {
    setSchreibenEditId(null);
    setSchreibenEditText('');
    setSchreibenPlaceholderKind(null);
  }, []);

  const stripPortalShareUrls = (message: string): string =>
    message
      .replace(/Direktlink:\s*https?:\/\/\S+/gi, '')
      .replace(/https?:\/\/[^\s]*cloud[^\s]*/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();

  // Stream 3: Schreiben-Entwurf als PDF (Gotenberg via Parent). Quelle = sichtbarer
  // Chat-Text (nach Inline-Edit), nicht der Roh-Draft mit unausgefüllten Klammern.
  const handleExportSchreibenPdf = useCallback(async (msg: Message, opts?: { skipPlaceholderConfirm?: boolean }) => {
    if (!onExportSchreibenPdf || !msg.id || exportingPdfId) return;
    const raw = commitSchreibenEdit(msg);
    const pdfText = sanitizeSchreibenExportText(raw);
    if (!opts?.skipPlaceholderConfirm && hasUnfilledSchreibenPlaceholders(pdfText)) {
      setSchreibenPlaceholderKind('pdf');
      if (schreibenEditId !== msg.id) {
        setSchreibenEditId(msg.id);
        setSchreibenEditText(raw);
      }
      return;
    }
    setSchreibenPlaceholderKind(null);
    setExportingPdfId(msg.id);
    try {
      const res = await onExportSchreibenPdf({
        text: pdfText,
        displayName: schreibenExportDisplayName('pdf'),
      });
      const safeText = stripPortalShareUrls(res.message);
      const reply = mkMsg(
        'bot',
        safeText || (res.ok ? 'PDF erstellt und in Ihrer Akte abgelegt.' : 'PDF-Export fehlgeschlagen.'),
      );
      if (res.ok && res.portalDocument) {
        reply.portalDocumentOpen = res.portalDocument;
      }
      updateMessages((prev) => [...prev, reply]);
    } catch (err) {
      updateMessages((prev) => [
        ...prev,
        mkMsg('bot', err instanceof Error ? err.message : 'PDF-Export fehlgeschlagen.'),
      ]);
    } finally {
      setExportingPdfId(null);
    }
  }, [onExportSchreibenPdf, exportingPdfId, updateMessages, commitSchreibenEdit, schreibenEditId]);

  const handleExportSchreibenDocx = useCallback(async (
    msg: Message,
    opts?: { skipPlaceholderConfirm?: boolean; standPrefix?: boolean },
  ) => {
    if (!onExportSchreibenDocx || !msg.id || exportingDocxId) return;
    const raw = commitSchreibenEdit(msg);
    const plain = sanitizeSchreibenExportText(raw);
    const withStand = Boolean(opts?.standPrefix);
    const docxText = withStand ? prefixSavedChatStatus(plain) : plain;
    if (!opts?.skipPlaceholderConfirm && hasUnfilledSchreibenPlaceholders(plain)) {
      setSchreibenPlaceholderKind('docx');
      if (schreibenEditId !== msg.id) {
        setSchreibenEditId(msg.id);
        setSchreibenEditText(raw);
      }
      return;
    }
    setSchreibenPlaceholderKind(null);
    setExportingDocxId(msg.id);
    try {
      const res = await onExportSchreibenDocx({
        text: docxText,
        displayName: schreibenExportDisplayName('docx'),
      });
      const safeText = stripPortalShareUrls(res.message);
      const standWord = withStand ? savedChatStand(plain) : '';
      const base = safeText || (res.ok ? 'DOCX in der Akte abgelegt und im Portal geöffnet.' : 'DOCX-Export fehlgeschlagen.');
      const reply = mkMsg('bot', standWord ? `${base} ${standWord}.` : base);
      if (res.ok && res.portalDocument) {
        reply.portalDocumentOpen = res.portalDocument;
      }
      updateMessages((prev) => [...prev, reply]);
    } catch (err) {
      updateMessages((prev) => [
        ...prev,
        mkMsg('bot', err instanceof Error ? err.message : 'DOCX-Export fehlgeschlagen.'),
      ]);
    } finally {
      docxStandPrefixRef.current = false;
      setExportingDocxId(null);
    }
  }, [onExportSchreibenDocx, exportingDocxId, updateMessages, commitSchreibenEdit, schreibenEditId]);

  const handleSaveSchreibenToAkte = useCallback((msg: Message) => {
    const sink = resolveSaveToFileChipSink({
      akteBound,
      hasDocxExport: Boolean(onExportSchreibenDocx),
    });
    if (sink === 'hidden') return;
    if (sink === 'prompt_open_akte') {
      updateMessages((prev) => [
        ...prev,
        mkMsg(
          'bot',
          'Bitte zuerst eine Akte öffnen — dann speichere ich das Schreiben im Aktenspeicher.',
        ),
      ]);
      return;
    }
    docxStandPrefixRef.current = true;
    void handleExportSchreibenDocx(msg, { standPrefix: true });
  }, [akteBound, handleExportSchreibenDocx, onExportSchreibenDocx, updateMessages]);

  const handleApplySynthesisToDocument = useCallback(
    async (msg: Message, target: 'existing' | 'new') => {
      if (!onApplySynthesisToDocument || !msg.id || applyingSynthesis) return;
      setApplyingSynthesis({ messageId: msg.id, target });
      try {
        const res = await onApplySynthesisToDocument({ text: msg.text, target });
        setSynthesisRetryIds((prev) => {
          const next = new Set(prev);
          if (res.ok) next.delete(msg.id);
          else next.add(msg.id);
          return next;
        });
        updateMessages((prev) => [...prev, mkMsg('bot', res.message)]);
      } catch (err) {
        setSynthesisRetryIds((prev) => new Set(prev).add(msg.id));
        updateMessages((prev) => [
          ...prev,
          mkMsg(
            'bot',
            err instanceof Error ? err.message : 'Entwurf konnte nicht übernommen werden.',
          ),
        ]);
      } finally {
        setApplyingSynthesis(null);
      }
    },
    [onApplySynthesisToDocument, applyingSynthesis, updateMessages],
  );

  // Stream 5 — Proaktive Quick-Actions: jeder Chip löst einen BESTEHENDEN Flow aus.
  //  lookup_law   -> vorgefertigte Rechtsfrage-Folgenachricht (LEXCORE-Pfad)
  //  ogh_dazu     -> JUD /v1/jud/answer nach LEX-Treffern (lexCarry, Trail JUD)
  //  web_recherche -> ARKORBIT-Web-Gate (keine Akte, keine PII-URLs)
  //  export_pdf   -> Schreiben-PDF-Export (Stream 3, Gotenberg)
  //  save_to_file -> Schreiben-Datei in die gebundene Akte (DOCX, nicht MEMCORE)
  //  refine_letter-> Inline-Editor im Schreiben-Kasten (sichtbarer Text = Export-Quelle)
  //  variant_1/2/3 -> Allrounder-Follow-up; mit Auszügen an den angehakten Docs (kein LEXCORE)
  //  pick_variant  -> Legacy: parseSuggestedActions expandiert zu Variante 1/2/3
  const handleSuggestedAction = useCallback((action: ArkoQuickActionType, msg: Message) => {
    if (isLoading) return;
    switch (action) {
      case 'export_pdf':
        void handleExportSchreibenPdf(msg);
        break;
      case 'save_to_file':
        // Vertrag 1: nie onMemorySaveRequest / MEMCORE — Marker muss im Dist liegen.
        if (PORTAL_AKTE_CHIP_SINK_MARKER === 'akte-chip-sink:bound_akte') {
          handleSaveSchreibenToAkte(msg);
        }
        break;
      case 'lookup_law': {
        let lastUserText = '';
        const hist = messagesRef.current;
        for (let i = hist.length - 1; i >= 0; i--) {
          if (hist[i].role === 'user' && hist[i].text.trim()) {
            lastUserText = stripQuickActionPrefix(hist[i].text);
            break;
          }
        }
        const base = lastUserText.trim() || 'Welche Paragraphen sind hier einschlägig?';
        void sendMessage(RECHTSFRAGE_ACTION.prefix + base, 'rechtsfrage');
        break;
      }
      case 'ogh_dazu': {
        if (msg.id && oghJoinLexIdsRef.current.has(msg.id)) break;
        let lastUserText = '';
        const hist = messagesRef.current;
        for (let i = hist.length - 1; i >= 0; i--) {
          if (hist[i].role === 'user' && hist[i].text.trim()) {
            lastUserText = stripQuickActionPrefix(hist[i].text);
            break;
          }
        }
        const carry = sanitizeLexCarry(msg.lexProof);
        const ids = lexCarryNormIds(carry);
        const prompt = buildOghFollowupPrompt(lastUserText, ids);
        if (!prompt || !ids.length) break;
        if (msg.id) markOghJoinForLexMsg(msg.id);
        void sendMessage(prompt, 'judicature_recherche', {
          chipAction: 'ogh_dazu',
          lexCarry: carry,
          hideUserBubble: !shouldShowOghFollowupUserBubble('chip'),
        });
        break;
      }
      case 'web_recherche': {
        const follow = msg.suggestedActions?.find((item) => item.action === 'web_recherche')?.followupQuery?.trim() || '';
        let lastUserText = follow;
        if (!lastUserText) {
          const hist = messagesRef.current;
          for (let i = hist.length - 1; i >= 0; i--) {
            if (hist[i].role === 'user' && hist[i].text.trim()) {
              lastUserText = stripQuickActionPrefix(hist[i].text);
              break;
            }
          }
        }
        const req = webSearchLookupRequest(lastUserText);
        if (!req) break;
        void sendMessage(req.chatInput, req.intent, {
          chipAction: 'web_recherche',
          omitDocumentContext: true,
        });
        break;
      }
      case 'variant_1':
      case 'variant_2':
      case 'variant_3': {
        const n = action === 'variant_1' ? 1 : action === 'variant_2' ? 2 : 3;
        setPendingIntent('');
        const ctx = documentContextEnabled ? String(documentContext || '') : '';
        const parsed = parseAkteSelectedExtractsFromDocumentContext(ctx);
        const prompt = parsed.length > 0
          ? buildAkteVariantDeepenPrompt(
            n,
            parsed.map((p) => p.title),
            parsed.map((p) => p.extract),
          )
          : {
            text: `Bitte vertiefen Sie Variante ${n} aus Ihrer vorherigen Analyse.`,
            intent: 'erklaeren' as const,
          };
        void sendMessage(prompt.text, prompt.intent, { chipAction: action });
        break;
      }
      case 'pick_variant':
        break;
      case 'refine_letter':
        if (msg.id && schreibenEditId === msg.id) {
          finishSchreibenEdit(msg);
        } else {
          startSchreibenEdit(msg);
        }
        break;
      case 'schreiben_erstellen': {
        const follow = msg.suggestedActions?.find((item) => item.action === 'schreiben_erstellen')?.followupQuery?.trim() || '';
        let lastUserText = follow;
        if (!lastUserText) {
          const hist = messagesRef.current;
          for (let i = hist.length - 1; i >= 0; i--) {
            if (hist[i].role === 'user' && hist[i].text.trim()) {
              lastUserText = stripQuickActionPrefix(hist[i].text);
              break;
            }
          }
        }
        if (!lastUserText.trim()) break;
        void sendMessage(lastUserText, 'schreiben_erstellen', { chipAction: 'schreiben_erstellen' });
        break;
      }
    }
  }, [
    isLoading,
    handleExportSchreibenPdf,
    handleSaveSchreibenToAkte,
    sendMessage,
    schreibenEditId,
    finishSchreibenEdit,
    startSchreibenEdit,
    documentContext,
    documentContextEnabled,
    markOghJoinForLexMsg,
  ]);

  const handleDocumentWelcomeAction = useCallback((actionId: DocumentWelcomeActionId) => {
    if (isLoading) return;
    const docLabel = documentDisplayName?.trim() || 'das geöffnete Dokument';
    switch (actionId) {
      case 'erklaeren':
        setPendingIntent('');
        setPendingDocumentQuestion(true);
        setInput('');
        focusChatInput();
        break;
      case 'bearbeiten':
        updateMessages((prev) => [
          ...prev,
          mkMsg(
            'bot',
            `Sie können „${docLabel}" direkt im Editor links bearbeiten (OnlyOffice). ` +
              'Speichern Sie dort — ich beziehe mich im Chat auf den aktuellen Stand, sobald der Text extrahiert ist.',
          ),
        ]);
        break;
      case 'fristen':
        void sendMessage(
          'Bitte prüfen Sie Fristen und Zustellungen in diesem Dokument.',
          'frist_vorschlag',
        );
        break;
      case 'zusammenfassen':
        void sendMessage(
          'Fasse den Inhalt des geöffneten Dokuments präzise zusammen.',
          'zusammenfassen',
        );
        break;
    }
  }, [isLoading, documentDisplayName, sendMessage, updateMessages, focusChatInput]);

  const handleWebArticleAction = useCallback((
    actionId: WebArticleActionId,
    article: WebArticleFetchResult,
    userIntent?: string,
  ) => {
    if (isLoading) return;
    const docContext = buildWebArticleDocumentContext(article);
    const meta = webArticleMetaForTrace(article);
    pendingWebArticleRef.current = null;

    switch (actionId) {
      case 'cancel':
        updateMessages((prev) => [
          ...prev,
          mkMsg('bot', 'Web-Artikel-Aktion abgebrochen.'),
        ]);
        break;
      case 'show_full':
        updateMessages((prev) => [
          ...prev,
          mkMsg('bot', docContext),
        ]);
        break;
      case 'new_document':
        if (!onApplySynthesisToDocument) {
          updateMessages((prev) => [
            ...prev,
            mkMsg('bot', 'Dokument-Übernahme ist in dieser Ansicht nicht verfügbar.'),
          ]);
          break;
        }
        void (async () => {
          beginLoading({ flow: 'synthesis_apply', chatInput: docContext });
          try {
            const result = await onApplySynthesisToDocument({
              text: docContext,
              target: 'new',
            });
            updateMessages((prev) => [...prev, mkMsg('bot', result.message)]);
          } catch {
            updateMessages((prev) => [
              ...prev,
              mkMsg('bot', 'Der Entwurf konnte nicht als Dokument angelegt werden.'),
            ]);
          } finally {
            endLoading();
          }
        })();
        break;
      case 'continue':
        void sendMessage(
          userIntent?.trim() || 'Bitte arbeite mit dem geladenen Web-Artikel weiter.',
          undefined,
          {
            documentContextOverride: docContext,
            webArticleMeta: meta,
            skipWebArticleDetection: true,
          },
        );
        break;
      case 'summarize':
      default:
        void sendMessage(
          userIntent?.trim() || 'Fasse folgenden Web-Artikel präzise zusammen.',
          'zusammenfassen',
          {
            documentContextOverride: docContext,
            webArticleMeta: meta,
            skipWebArticleDetection: true,
          },
        );
        break;
    }
  }, [isLoading, onApplySynthesisToDocument, sendMessage, updateMessages, beginLoading, endLoading]);

  // Verfügbarkeits-Filter: Chips, deren Flow nicht verdrahtet ist, ausblenden.
  const visibleSuggestedActions = useCallback((msg: Message): SuggestedAction[] => {
    if (msg.role !== 'bot' || !msg.suggestedActions || !msg.text.trim()) return [];
    return msg.suggestedActions.filter((a) => {
      if (a.action === 'export_pdf') return Boolean(onExportSchreibenPdf) && msg.schreibenDraft === true;
      if (a.action === 'save_to_file') {
        return resolveSaveToFileChipSink({
          akteBound,
          hasDocxExport: Boolean(onExportSchreibenDocx),
        }) !== 'hidden' && Boolean(msg.id);
      }
      if (a.action === 'ogh_dazu' && msg.id && oghJoinLexIds.has(msg.id)) return false;
      return true;
    });
  }, [akteBound, onExportSchreibenPdf, onExportSchreibenDocx, oghJoinLexIds]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.altKey) return;
    if (e.shiftKey) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const el = textareaRef.current;
      if (!el) return;
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const next = `${input.slice(0, start)}\n${input.slice(end)}`;
      setInput(next);
      requestAnimationFrame(() => {
        el.selectionStart = start + 1;
        el.selectionEnd = start + 1;
        adjustTextareaHeight();
      });
      return;
    }
    e.preventDefault();
    sendMessage(input);
  };

  const applyMemRecallChip = () => {
    if (pendingMemRecall) {
      setPendingMemRecall(false);
      setInput(prev => stripMemRecallPrefix(prev));
      textareaRef.current?.focus();
      return;
    }
    setInput(prev => {
      const stripped = stripMemRecallPrefix(stripQuickActionPrefix(prev));
      const base = stripped.trim() ? stripped : '';
      return MEM_RECALL_CHIP_PREFIX + base;
    });
    setPendingMemRecall(true);
    textareaRef.current?.focus();
  };

  const applyQuickAction = (action: QuickAction) => {
    setPendingDocumentQuestion(false);
    if (pendingIntent === action.intent) {
      setPendingIntent('');
      setInput(prev => stripQuickActionPrefix(prev));
      textareaRef.current?.focus();
      return;
    }

    setInput(prev => {
      const stripped = stripQuickActionPrefix(prev);
      const base = stripped.trim() ? stripped : '';
      return action.prefix + base;
    });
    setPendingIntent(action.intent);
    textareaRef.current?.focus();
  };

  const startContractFromChip = () => {
    if (!enableContractIntent || !onContractGenerate || isLoading) return;
    setContractAskOpen((open) => !open);
  };

  const openContractCreator = (templateId: string) => {
    if (!enableContractIntent || !onContractGenerate || isLoading) return;
    setContractAskOpen(false);
    window.dispatchEvent(
      new CustomEvent(ARKO_OPEN_CONTRACT_CREATOR_EVENT, { detail: { templateId } }),
    );
  };

  // F2 — Chip-Logik: nur wenn die letzte Eingabe weder explizit als Rechtsfrage
  // markiert wurde noch der Heuristik-Trigger greift, bekommt der Nutzer den
  // optionalen Re-Routing-Vorschlag angeboten.
  const lastMsg = messages[messages.length - 1];
  let lastUserMsg: Message | null = null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') { lastUserMsg = messages[i]; break; }
  }
  const rechtsfragePrefixHead = RECHTSFRAGE_ACTION.prefix.split('\n')[0].toLowerCase();
  const showLegalChip =
    showQuickActions &&
    showRechtsfrageQuickAction &&
    !isLoading &&
    lastMsg?.role === 'bot' &&
    lastUserMsg !== null &&
    !lastUserMsg.text.trimStart().toLowerCase().startsWith(rechtsfragePrefixHead) &&
    !looksLikeLegalQuestion(lastUserMsg.text);

  const recheckAsLegalQuestion = () => {
    if (!lastUserMsg || isLoading) return;
    const newText = RECHTSFRAGE_ACTION.prefix + lastUserMsg.text;
    sendMessage(newText, 'rechtsfrage');
  };

  const contractTypeAsk = contractAskOpen ? (
    <div className="mb-2 px-1" role="group" aria-label="Welchen Vertrag?">
      <p className="mb-1.5 text-xs font-semibold text-slate-100">Welchen Vertrag?</p>
      <div className="flex flex-wrap gap-1.5">
        {CONTRACT_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => openContractCreator(tpl.id)}
            className="rounded-full bg-white/[0.04] px-2.5 py-1 text-[11px] text-slate-200 hover:bg-violet-500/15 hover:text-white"
          >
            {tpl.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setContractAskOpen(false)}
          className="px-2 py-1 text-[11px] text-slate-500 hover:text-slate-300"
        >
          Abbrechen
        </button>
      </div>
    </div>
  ) : null;

  const cursorToolsBar = showQuickActions ? (
    <PortalChipBar
      ref={chipBarRef}
      accent={resolvedChipAccent}
      legacyQaClassName={CURSOR_PORTAL.chip}
      compactChrome={compactChrome}
      className="!px-0 !pt-0"
      schnellContent={
        <>
          {documentQuickActions.map((action) => (
            <PortalChip
              key={action.label}
              label={action.label}
              icon={action.icon}
              accent={resolvedChipAccent}
              selected={pendingIntent === action.intent}
              title={ARKO_TOOL_HINTS[action.label]}
              onClick={() => applyQuickAction(action)}
            />
          ))}
          {enableContractIntent && onContractGenerate && (
            <PortalChip
              key="Vertrag erstellen"
              label="Vertrag erstellen"
              icon={FileSignature}
              accent={resolvedChipAccent}
              title={ARKO_TOOL_HINTS['Vertrag erstellen']}
              aria-expanded={contractAskOpen}
              onClick={startContractFromChip}
            />
          )}
          {memcoreRecall && (
            <PortalChip
              key={MEM_RECALL_CHIP_LABEL}
              label={MEM_RECALL_CHIP_LABEL}
              icon={Brain}
              accent={resolvedChipAccent}
              selected={pendingMemRecall}
              title={MEM_RECALL_CHIP_HINT}
              onClick={applyMemRecallChip}
            />
          )}
        </>
      }
      rechtContent={
        <>
          {showRechtsfrageQuickAction && (
            <PortalChip
              key={RECHTSFRAGE_ACTION.label}
              label={RECHTSFRAGE_ACTION.label}
              icon={RECHTSFRAGE_ACTION.icon}
              accent={resolvedChipAccent}
              selected={pendingIntent === RECHTSFRAGE_ACTION.intent}
              title={ARKO_TOOL_HINTS[RECHTSFRAGE_ACTION.label]}
              onClick={() => applyQuickAction(RECHTSFRAGE_ACTION)}
            />
          )}
          {showLegalChip && (
            <PortalChip
              key="Als Rechtsfrage prüfen?"
              label="Als Rechtsfrage prüfen?"
              icon={Scale}
              accent={resolvedChipAccent}
              title={ARKO_TOOL_HINTS['Als Rechtsfrage prüfen?']}
              onClick={recheckAsLegalQuestion}
            />
          )}
        </>
      }
    />
  ) : null;

  const chatInputPlaceholder = pendingDocumentQuestion
    ? DOCUMENT_QUESTION_INPUT_PLACEHOLDER
    : compactChrome
      ? 'Nachricht… (Enter senden, / fokussieren)'
      : showQuickActions
        ? 'Text eingeben… (Enter senden, / fokussieren, Esc schließen)'
        : 'Nachricht eingeben… (Enter senden, / fokussieren)';
  const handoffDocsHint = akteHandoff?.trim() && !(selectedDocuments?.length)
    ? AKTE_HANDOFF_DOCS_HINT
    : null;

  if (isCursorEmpty) {
    return (
      <div className={`${chatShell.shell} flex flex-col flex-1 min-h-0 h-full ${shellClassName}`}>
        {akteHandoff?.trim() ? (
          <div className={`px-3 pt-3 ${ARKO_CHAT_COLUMN_CLASS}`}>
            <AkteHandoffBanner text={akteHandoff} compact={compactChrome} hint={handoffDocsHint} />
          </div>
        ) : null}
        <span className="sr-only">{PORTAL_LEX_JUD_JOIN_MARKER} {PORTAL_LEX_JUD_JOIN_NO_GHOST_MARKER} {PORTAL_LEX_JUD_JOIN_VOICE_MARKER} {PORTAL_LEX_JUD_JOIN_TOPIC_MARKER} {PORTAL_LEX_JUD_JOIN_HEADNOTE_MARKER} {PORTAL_LEX_JUD_JOIN_FRAGEPUNKT_MARKER}</span>
        {contractTypeAsk}
        {hauptLook ? <div className="min-h-0 flex-1" /> : null}
        <ArkoCursorComposer
          ref={composerRef}
          customerName={cursorCustomerName}
          kanzlei={cursorKanzlei}
          placeholder={chatInputPlaceholder}
          compact={compactChrome}
          layout={hauptLook ? 'bottom' : 'center'}
          showToolsTray={showQuickActions}
          toolsBar={cursorToolsBar}
          isLoading={isLoading}
          onCancel={cancelRequest}
          attachedParagraphs={attachedParagraphs}
          onAttachedParagraphsChange={handleAttachedParagraphsChange}
          onParagraphLookup={handleParagraphLookup}
          onJudicatureLookup={handleJudicatureLookup}
          onWebSearchLookup={handleWebSearchLookup}
          relatedParagraphs={relatedParagraphs}
          contextMeter={arkoContextMeter}
          onContinueAkte={onContinueAkte}
          savedModeId={hubModeId}
          onModeCommit={onHubModeCommit}
          appearance={appearance}
          onSubmit={(submitText, mode, attachmentFiles) => {
            void handleComposerSubmit(submitText, mode, attachmentFiles);
          }}
        />
        <ArkoChatAiActNotice compact={compactChrome} className="flex-shrink-0 max-w-md mx-auto" />
      </div>
    );
  }

  return (
    <div
      className={`${
        isCursor ? `${chatShell.shell} flex flex-col flex-1 min-h-0 h-full` : PORTAL_CHAT.shell
      } ${isCursor ? '' : 'min-h-[520px]'} ${shellClassName}`}
    >
      {/* Header */}
      {!hideHeader && (
      <div className={`p-4 ${PORTAL_CHAT.header} flex items-center space-x-3`}>
        {headerAvatar ?? (
          /* Probe / WIP Mascot v4 — ersetzt Sparkles-Header-Avatar */
          <ArkoMascotAvatar sizeClassName="w-9 h-9" className="rounded-xl" />
        )}
        <div className="flex-1">
          <h4 className="text-white font-bold text-sm">{title}</h4>
          <div className="flex items-center space-x-1.5">
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${accent.dot}`} />
            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">{subtitle}</span>
          </div>
        </div>
        <div className="flex items-center space-x-1 text-[9px] text-slate-600 uppercase tracking-widest">
          <Shield className="w-3 h-3" />
          <span>Lokal · Frankfurt</span>
        </div>
      </div>
      )}

      {/* Messages */}
      <div
        ref={scrollRef}
        data-arko-chat-scroll={PORTAL_CHAT_SCROLL_FOLLOW_MARKER}
        onWheel={(e) => {
          if (shouldDetachFollowForWheel(e.deltaY)) detachChatFollow();
        }}
        onTouchMove={() => detachChatFollow()}
        onKeyDown={(e) => {
          if (shouldDetachFollowForKey(e.key)) detachChatFollow();
        }}
        onScroll={(e) => {
          stickToBottomRef.current = followAfterUserScroll(e.currentTarget);
        }}
        style={{ scrollBehavior: 'auto' }}
        className={`flex-1 overflow-y-auto custom-scrollbar min-h-0 ${
          isCursor
            ? `px-3 sm:px-4 py-4 space-y-3 ${ARKO_CHAT_COLUMN_CLASS}`
            : compactChrome
              ? 'p-2 space-y-2'
              : 'p-4 space-y-3'
        }`}
      >
        {akteHandoff?.trim() ? (
          <AkteHandoffBanner text={akteHandoff} compact={compactChrome} hint={handoffDocsHint} />
        ) : null}
        {messages.map((msg, i) => {
          const suggestedChips = visibleSuggestedActions(msg);
          const taskFollowups = suggestedChips.filter(
            (chip) => chip.action === 'web_recherche' && Boolean(chip.followupQuery?.trim()),
          );
          const barChips = suggestedChips.filter(
            (chip) => !(chip.action === 'web_recherche' && Boolean(chip.followupQuery?.trim())),
          );
          const answerPathStyle =
            msg.role === 'bot' ? getAnswerPathStyleFromTrace(msg.reasoningTrace) : null;
          if (msg.messageKind === 'document_focus_switch') {
            return (
              <div key={i} className="flex w-full justify-center py-1">
                <span className="text-[11px] text-violet-300/80 bg-violet-500/10 border border-violet-500/20 rounded-full px-3 py-1">
                  {msg.text}
                </span>
              </div>
            );
          }
          return (
          <div
            key={i}
            className={`flex w-full flex-col ${hauptLook ? 'items-stretch' : msg.role === 'user' ? 'items-end' : 'items-start'}`}
            {...(hauptLook ? { 'data-portal-haupt-follow': '1' } : {})}
          >
            <div className={`flex w-full ${hauptLook || msg.role !== 'user' ? 'justify-start' : 'justify-end'}`}>
              {msg.role === 'bot' && !hauptLook && (
                /* Probe / WIP Mascot v4 — ersetzt Lucide Bot in Bubbles */
                <ArkoMascotAvatar sizeClassName="w-6 h-6" className="mr-2 mt-1" />
              )}
              <div className={`${
                hauptLook
                  ? 'w-full max-w-full'
                  : msg.schreibenDraft || msg.mandatsConfirm
                    ? 'max-w-[min(100%,40rem)] w-full'
                    : 'max-w-[80%]'
              } flex flex-col gap-0.5 min-w-0 ${
                hauptLook || msg.role !== 'user' ? 'items-stretch' : 'items-end'
              }`}>
                {msg.role === 'bot' && answerPathStyle ? (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider pl-0.5"
                    style={{ color: answerPathStyle.labelColor }}
                    aria-label={`Antwort-Pfad: ${answerPathStyle.label}`}
                  >
                    {answerPathStyle.label}
                  </span>
                ) : null}
                {msg.role === 'bot' && msg.streamBadge ? (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider pl-0.5 text-amber-200/90"
                    aria-label="Qualitätsprüfung läuft"
                  >
                    {msg.streamBadge}
                  </span>
                ) : null}
                {msg.role === 'bot' && msg.schreibenDraft ? (
                  <div
                    className={`w-full overflow-hidden ${
                      hauptLook
                        ? 'bg-transparent text-slate-200'
                        : isCursor ? cursorBotBubble : PORTAL_CHAT.botBubble
                    }`}
                  >
                    <div className={`flex items-center justify-between gap-2 ${hauptLook ? 'px-0.5 py-1' : 'border-b border-amber-500/20 bg-amber-500/[0.07] px-3 py-1.5'}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-200/90">
                        Schreiben
                      </span>
                      {schreibenEditId === msg.id ? (
                        <span className="text-[10px] text-amber-200/70">Platzhalter in [Klammern] ausfüllen</span>
                      ) : (
                        <span className="text-[10px] text-slate-500">Entwurf — vor PDF/DOCX prüfen</span>
                      )}
                    </div>
                    {schreibenEditId === msg.id ? (
                      <textarea
                        value={schreibenEditText}
                        onChange={(e) => setSchreibenEditText(e.target.value)}
                        rows={12}
                        className={`w-full min-h-[12rem] resize-y bg-transparent text-sm leading-relaxed text-slate-200 outline-none placeholder:text-slate-600 ${hauptLook ? 'px-0.5 py-1' : 'px-3 py-2.5'}`}
                        aria-label="Schreiben-Entwurf bearbeiten"
                        spellCheck
                      />
                    ) : (
                      <div className={`text-sm leading-relaxed whitespace-pre-wrap ${hauptLook ? 'px-0.5 py-1' : 'p-3'}`}>
                        {displayArkoTurnText('bot', formatBotMessageForDisplay(msg.text, msg.lexProof, msg.judProof, msg.webProof))}
                      </div>
                    )}
                    {schreibenEditId === msg.id ? (
                      <div className={`flex flex-wrap gap-1.5 ${hauptLook ? 'px-0.5 py-1' : 'border-t border-white/[0.06] px-3 py-2'}`}>
                        <button
                          type="button"
                          onClick={() => finishSchreibenEdit(msg)}
                          className="flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] text-emerald-100 hover:bg-emerald-500/20"
                        >
                          <Check className="h-3 w-3" />
                          Fertig
                        </button>
                        <button
                          type="button"
                          onClick={cancelSchreibenEdit}
                          className={hauptLook ? 'rounded-md border border-violet-400/40 bg-[#07070a] px-2 py-1 text-[11px] text-violet-100 hover:bg-violet-600/30' : 'rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-slate-300 hover:bg-white/[0.08]'}
                        >
                          Abbrechen
                        </button>
                      </div>
                    ) : null}
                    {schreibenEditId === msg.id && schreibenPlaceholderKind ? (
                      <div className="border-t border-amber-500/25 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-100">
                        <p>Noch unausgefüllte Platzhalter in [eckigen Klammern]. Trotzdem {schreibenPlaceholderKind === 'pdf' ? 'als PDF' : 'als DOCX'} speichern?</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (schreibenPlaceholderKind === 'docx') {
                              void handleExportSchreibenDocx(msg, {
                                skipPlaceholderConfirm: true,
                                standPrefix: docxStandPrefixRef.current,
                              });
                            }
                              else void handleExportSchreibenPdf(msg, { skipPlaceholderConfirm: true });
                            }}
                            className="rounded-md border border-amber-400/40 bg-amber-500/20 px-2 py-1 text-amber-50 hover:bg-amber-500/30"
                          >
                            Trotzdem speichern
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              docxStandPrefixRef.current = false;
                              setSchreibenPlaceholderKind(null);
                            }}
                            className="rounded-md border border-white/10 px-2 py-1 text-slate-300 hover:bg-white/[0.06]"
                          >
                            Zuerst ausfüllen
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : msg.role === 'bot' && msg.mandatsConfirm ? (
                  <PortalMandatsConfirmCard
                    slots={msg.mandatsConfirm}
                    editing={mandatsEditId === msg.id}
                    draft={mandatsEditId === msg.id ? mandatsEditDraft : msg.mandatsConfirm}
                    disabled={isLoading}
                    onStartEdit={() => startMandatsConfirmEdit(msg)}
                    onDraftChange={updateMandatsConfirmDraft}
                    onSave={() => commitMandatsConfirmEdit(msg)}
                    onCancel={cancelMandatsConfirmEdit}
                  />
                ) : (
                <div
                  {...(msg.role === 'bot' ? { 'data-portal-arko-bot': '' } : {})}
                  className={`${hauptLook && msg.role === 'bot' ? 'px-0.5 py-1' : 'p-3'} text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? hauptLook
                        ? hauptUserBubble
                        : isCursor ? CURSOR_CHAT.userBubble : accent.userBubble
                      : hauptLook
                        ? hauptBotPlain
                        : isCursor ? cursorBotBubble : PORTAL_CHAT.botBubble
                  }`}
                  style={
                    msg.role === 'bot' && answerPathStyle && !hauptLook
                      ? {
                          borderLeftWidth: '3px',
                          borderLeftStyle: 'solid',
                          borderLeftColor: answerPathStyle.borderColor,
                        }
                      : undefined
                  }
                >
                  {msg.role === 'bot'
                    ? (
                      <>
                        {shouldRenderLexQuoteAsLead(msg.lexJudJoinVoice) ? (
                          <LexVerifiedQuoteBlock lexProof={msg.lexProof} />
                        ) : null}
                        {displayArkoTurnText('bot', formatBotMessageForDisplay(msg.text, msg.lexProof, msg.judProof, msg.webProof))}
                        {msg.lexJudJoinVoice ? (
                          <LexJudJoinFolds lexProof={msg.lexProof} judProof={msg.judProof} />
                        ) : null}
                        <WebRechercheFold webProof={msg.webProof} />
                      </>
                    )
                    : displayArkoTurnText('user', msg.text)}
                  {msg.role === 'bot' && msg.backgroundAnalysis?.details ? (
                    <details className="mt-2 border-t border-white/10 pt-2">
                      <summary className="cursor-pointer select-none text-[11px] font-medium text-violet-200/90 hover:text-violet-100">
                        Details zur Analyse
                        {msg.backgroundAnalysis.documentName
                          ? ` · ${msg.backgroundAnalysis.documentName}`
                          : ''}
                      </summary>
                      <div className="mt-2 whitespace-pre-wrap text-[12px] leading-relaxed text-slate-200/90">
                        {msg.backgroundAnalysis.details}
                      </div>
                    </details>
                  ) : null}
                </div>
                )}
              </div>
            </div>
            {hauptLook && msg.role === 'bot' && i > 0 && msg.text.trim() && !isArkoSystemNotice(msg.text) && !msg.mandatsConfirm ? (
              <div className="mt-1 flex w-full items-start gap-0.5 pl-0.5" data-testid="portal-haupt-answer-tools">
                <button
                  type="button"
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-violet-300/90 transition-colors hover:bg-violet-600/20 hover:text-violet-100"
                  aria-label="Antwort kopieren"
                  title={copiedAnswerKey === (msg.id ?? `idx-${i}`) ? 'Kopiert' : 'Kopieren'}
                  onClick={() => {
                    const key = msg.id ?? `idx-${i}`;
                    const text = displayArkoTurnText('bot', schreibenSourceText(msg)).trim();
                    if (!text || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return;
                    void navigator.clipboard.writeText(text).then(() => {
                      setCopiedAnswerKey(key);
                      window.setTimeout(() => {
                        setCopiedAnswerKey((current) => (current === key ? null : current));
                      }, 1600);
                    }).catch(() => {
                      setCopiedAnswerKey(null);
                    });
                  }}
                >
                  {copiedAnswerKey === (msg.id ?? `idx-${i}`) ? (
                    <Check className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  )}
                </button>
                {demoSupportEnabled && demoSupportCustomerId ? (
                  <ArkoMessageFeedback
                    customerId={demoSupportCustomerId}
                    trace={msg.reasoningTrace}
                    uiDiag={messageFeedbackUiDiag}
                    iconsOnly
                  />
                ) : null}
              </div>
            ) : null}
            {msg.role === 'bot' && i > 0 && (msg.text.trim() || hasLexQuoteBlock(msg.lexProof)) && !isArkoSystemNotice(msg.text) && !msg.mandatsConfirm ? (
              <div className="flex w-full justify-start pl-8 mt-1">
                <ArkoAnswerLabels
                  text={msg.text}
                  lexProof={msg.lexProof}
                  judProof={msg.judProof}
                  webProof={msg.webProof}
                  reasoningTrace={msg.reasoningTrace}
                />
              </div>
            ) : null}
            {msg.role === 'user' && msg.paragraphRef ? (
              <div className="flex w-full justify-end pr-1 mt-1">
                <PortalParagraphContextChip entry={msg.paragraphRef} />
              </div>
            ) : null}
            {msg.role === 'bot' && !msg.lexJudJoinVoice && hasArkoProof(msg.text, msg.memProof, msg.lexProof, msg.judProof, msg.webProof) ? (
              <div className="flex w-full justify-start pl-8">
                <ArkoResponseProof
                  text={msg.text}
                  memProof={msg.memProof}
                  lexProof={msg.lexProof}
                  judProof={msg.judProof}
                  webProof={msg.webProof}
                  lawyerFacing
                />
              </div>
            ) : null}
            {msg.role === 'bot' && msg.reasoningTrace ? (
              <div className="flex w-full justify-start pl-8">
                <ArkoReasoningTraceCard
                  trace={msg.reasoningTrace}
                  facts={reasoningFactsForTurn({
                    answerText: msg.text,
                    lexProof: msg.lexProof,
                    judProof: msg.judProof,
                    webProof: msg.webProof,
                  })}
                  onFeedbackConfirmed={(confirmation) => {
                    updateMessages((prev) => [...prev, mkMsg('bot', confirmation)]);
                  }}
                />
              </div>
            ) : null}
            {!hauptLook && demoSupportEnabled && demoSupportCustomerId && msg.role === 'bot' && i > 0 && msg.text.trim() && !isArkoSystemNotice(msg.text) ? (
              <ArkoMessageFeedback
                customerId={demoSupportCustomerId}
                trace={msg.reasoningTrace}
                uiDiag={messageFeedbackUiDiag}
              />
            ) : null}
            {msg.role === 'bot' && msg.approvalFrist ? (
              <div className="flex w-full justify-start pl-8 max-w-[min(100%,28rem)]">
                <FristApprovalCard payload={msg.approvalFrist} variant="chat" />
              </div>
            ) : null}
            {msg.role === 'bot' && msg.messageKind === 'document_welcome' && msg.documentWelcomeActions?.length ? (
              <div className="flex w-full justify-start pl-8 mt-2 flex-wrap gap-1.5 max-w-[min(100%,32rem)]">
                {msg.documentWelcomeActions.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleDocumentWelcomeAction(chip.id)}
                    className={`rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                      hauptLook
                        ? 'border-violet-400/40 bg-violet-600/30 text-violet-100 hover:bg-violet-600/55'
                        : isCursor
                        ? 'border-[#57c06a]/30 bg-[#57c06a]/10 text-[#c8e6cc] hover:bg-[#57c06a]/20'
                        : 'border-white/10 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
            {msg.role === 'bot' && msg.structuredEdit && msg.id && !dismissedStructuredEditIds.has(msg.id) ? (
              <div className="flex w-full justify-start pl-8 mt-2">
                <PortalParagraphHitlPreview
                  edit={msg.structuredEdit}
                  paragraph={
                    msg.paragraphRef ??
                    effectiveParagraphMapForRender.find((p) => p.index === msg.structuredEdit!.index) ??
                    null
                  }
                  onDismiss={() => {
                    if (!msg.id) return;
                    setDismissedStructuredEditIds((prev) => new Set(prev).add(msg.id!));
                  }}
                />
              </div>
            ) : null}
            {msg.role === 'bot' && msg.guidedLaborDemo ? (
              <div className="flex w-full justify-start pl-8 mt-1">
                <span className="text-[10px] font-medium uppercase tracking-wider text-amber-400/90 bg-amber-500/10 border border-amber-500/25 rounded-full px-2 py-0.5">
                  Orientierung — ohne belegten Normenstand
                </span>
              </div>
            ) : null}
            {msg.role === 'bot' && msg.guidedRiskFindings?.length ? (
              <div className="flex w-full justify-start pl-8 mt-2">
                <PortalGuidedRiskPanel findings={msg.guidedRiskFindings} />
              </div>
            ) : null}
            {msg.role === 'bot' && msg.guidedFlowChips?.length ? (
              <div className="flex w-full justify-start pl-8 mt-2 flex-wrap gap-1.5 max-w-[min(100%,32rem)]">
                {msg.guidedFlowChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleGuidedFlowChip(chip, msg)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-50 ${
                      isCursor
                        ? 'border-violet-400/30 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20'
                        : 'border-violet-500/25 bg-violet-500/[0.08] text-violet-100 hover:bg-violet-500/15'
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
            {msg.role === 'bot' && msg.messageKind === 'web_article' && msg.webArticle ? (
              <div className="flex w-full justify-start pl-8 mt-2">
                <ArkoWebArticleActions
                  article={msg.webArticle}
                  disabled={isLoading}
                  hasOpenDocument={synthesisHasOpenDocument}
                  onAction={(actionId) => handleWebArticleAction(actionId, msg.webArticle!, msg.webArticleUserIntent)}
                />
              </div>
            ) : null}
            {/* Stream 3: Schreiben-Export ohne Chip-Leiste (Altbestand). */}
            {msg.role === 'bot' && msg.schreibenDraft && msg.text.trim() && suggestedChips.length === 0 ? (
              <div className="flex w-full flex-wrap gap-1.5 justify-start pl-8 mt-1">
                {onExportSchreibenPdf ? (
                  <button
                    type="button"
                    disabled={exportingPdfId === msg.id || isLoading}
                    onClick={() => void handleExportSchreibenPdf(msg)}
                    className={quietBtn}
                    title="Diesen Entwurf als PDF erstellen und in der Akte ablegen"
                    aria-label="Entwurf als PDF exportieren"
                  >
                    {exportingPdfId === msg.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <FileDown className="h-3 w-3" />
                    )}
                    Als PDF
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={isLoading || !msg.id}
                  onClick={() => (schreibenEditId === msg.id ? finishSchreibenEdit(msg) : startSchreibenEdit(msg))}
                  className={quietBtn}
                  title="Schreiben direkt im Chat ausfüllen"
                  aria-label="Schreiben anpassen"
                >
                  {schreibenEditId === msg.id ? <Check className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                  {hauptLook && schreibenEditId !== msg.id ? null : (schreibenEditId === msg.id ? 'Fertig' : 'Schreiben anpassen')}
                </button>
                {!hauptLook && onExportSchreibenDocx ? (
                  <button
                    type="button"
                    disabled={exportingDocxId === msg.id || isLoading}
                    onClick={() => handleSaveSchreibenToAkte(msg)}
                    className={quietBtn}
                    title="Schreiben als Datei in der gebundenen Akte ablegen"
                    aria-label="In Akte speichern"
                  >
                    {exportingDocxId === msg.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Save className="h-3 w-3" />
                    )}
                    In Akte speichern
                  </button>
                ) : null}
                {onExportSchreibenDocx ? (
                  <button
                    type="button"
                    disabled={exportingDocxId === msg.id || isLoading}
                    onClick={() => void handleExportSchreibenDocx(msg)}
                    className={quietBtn}
                    title="Als Word-Dokument in der Akte ablegen und im Portal öffnen"
                    aria-label="Entwurf als DOCX öffnen"
                  >
                    {exportingDocxId === msg.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <FilePlus className="h-3 w-3" />
                    )}
                    Als DOCX
                  </button>
                ) : null}
              </div>
            ) : null}
            {msg.role === 'bot' && msg.portalDocumentOpen && onOpenPortalDocument ? (
              <div className="flex w-full justify-start pl-8 mt-1.5 max-w-[min(100%,28rem)]">
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => void onOpenPortalDocument(msg.portalDocumentOpen!)}
                  className="flex w-full items-center gap-3 rounded-lg border border-emerald-500/25 bg-emerald-500/[0.07] px-3 py-2.5 text-left transition-colors hover:bg-emerald-500/15 hover:border-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-50"
                  title={`${portalDocumentKindLabel(msg.portalDocumentOpen.display_name)} im Portal öffnen`}
                  aria-label={`${portalDocumentKindLabel(msg.portalDocumentOpen.display_name)} öffnen: ${msg.portalDocumentOpen.display_name}`}
                >
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-emerald-500/30 bg-slate-950/40">
                    <FileText className="h-4 w-4 text-emerald-300" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12px] font-medium text-slate-100">
                      {msg.portalDocumentOpen.display_name}
                    </span>
                    <span className="block text-[10px] text-slate-400">
                      {portalDocumentKindLabel(msg.portalDocumentOpen.display_name)} · Im Portal öffnen
                    </span>
                  </span>
                </button>
              </div>
            ) : null}
            {onApplySynthesisToDocument && msg.role === 'bot' && msg.synthesisDraft && msg.text.trim() && !isArkoSystemNotice(msg.text) ? (
              <div className="flex w-full flex-wrap gap-1.5 justify-start pl-8 mt-1">
                {synthesisHasOpenDocument ? (
                  <button
                    type="button"
                    disabled={Boolean(applyingSynthesis) || isLoading}
                    onClick={() => void handleApplySynthesisToDocument(msg, 'existing')}
                    className="flex items-center gap-1.5 rounded-md border border-violet-500/25 bg-violet-500/10 px-2 py-1 text-[11px] text-violet-100 transition-colors hover:bg-violet-500/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    title="Synthese-Entwurf ans Ende des geöffneten Dokuments anhängen"
                    aria-label="In geöffnetes Dokument übernehmen"
                  >
                    {applyingSynthesis?.messageId === msg.id && applyingSynthesis.target === 'existing' ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                    {synthesisRetryIds.has(msg.id) ? 'Erneut: geöffnetes Dok.' : 'In geöffnetes Dokument'}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={Boolean(applyingSynthesis) || isLoading}
                  onClick={() => void handleApplySynthesisToDocument(msg, 'new')}
                  className="flex items-center gap-1.5 rounded-md border border-violet-500/20 bg-violet-500/[0.06] px-2 py-1 text-[11px] text-violet-100/90 transition-colors hover:bg-violet-500/15 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  title="Neues Synthese-Dokument anlegen und im Split öffnen"
                  aria-label="In neues Dokument übernehmen"
                >
                  {applyingSynthesis?.messageId === msg.id && applyingSynthesis.target === 'new' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <FilePlus className="h-3 w-3" />
                  )}
                  {synthesisRetryIds.has(msg.id) ? 'Erneut: neues Dok.' : 'In neues Dokument'}
                </button>
              </div>
            ) : null}
            {/* Stream 5: Proaktive Quick-Action-Chip-Leiste unter der Bot-Antwort.
                Löst bestehende Flows aus (Rechtsfrage/PDF/Akte/Anpassen). */}
            {msg.role === 'bot' && taskFollowups.length > 0 ? (
              <div className="flex w-full flex-wrap justify-start gap-2 pl-8 mt-3" data-testid="portal-task-followup">
                {taskFollowups.map((chip) => (
                  <button
                    key={`${chip.action}:${chip.followupQuery}`}
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleSuggestedAction(chip.action, msg)}
                    className={hauptLook
                      ? 'inline-flex items-center rounded-md border border-violet-400/40 bg-violet-600/30 px-3 py-1.5 text-sm font-semibold text-violet-100 hover:bg-violet-600/55 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'inline-flex items-center rounded-full border border-sky-400/45 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 disabled:cursor-not-allowed disabled:opacity-50'}
                    aria-label={chip.label}
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            ) : null}
            {msg.role === 'bot' && (barChips.length > 0 || Boolean(msg.schreibenDraft && onExportSchreibenDocx)) ? (
              <div className="flex w-full flex-wrap gap-1.5 justify-start pl-8 mt-1">
                {barChips.map((chip) => {
                  if (hauptLook && chip.action === 'save_to_file') return null;
                  const editingThis = chip.action === 'refine_letter' && schreibenEditId === msg.id;
                  const Icon = editingThis ? Check : ARKO_QUICK_ACTION_ICONS[chip.action];
                  const busy =
                    (chip.action === 'export_pdf' && exportingPdfId === msg.id)
                    || (chip.action === 'save_to_file' && exportingDocxId === msg.id);
                  const label = editingThis ? 'Fertig' : chip.label;
                  return (
                    <button
                      key={chip.action}
                      type="button"
                      disabled={isLoading || busy}
                      onClick={() => handleSuggestedAction(chip.action, msg)}
                      className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                        editingThis
                          ? 'border-amber-400/40 bg-amber-500/15 text-amber-100 hover:bg-amber-500/25'
                          : quietBtnIdle
                      }`}
                      title={
                        (chip.action === 'variant_1' || chip.action === 'variant_2' || chip.action === 'variant_3')
                        && documentContextEnabled
                        && parseAkteSelectedExtractsFromDocumentContext(documentContext).length > 0
                          ? `${label} anhand der angehakten Auszüge`
                          : label
                      }
                      aria-label={label}
                      {...(chip.action === 'save_to_file'
                        ? { 'data-akte-chip-sink': PORTAL_AKTE_CHIP_SINK_MARKER }
                        : {})}
                      {...((chip.action === 'variant_1' || chip.action === 'variant_2' || chip.action === 'variant_3')
                        && PORTAL_AKTE_VARIANT_EXTRACT_MARKER === 'akte-fuehren:varianten-auszuege'
                        ? { 'data-akte-variant-extract': PORTAL_AKTE_VARIANT_EXTRACT_MARKER }
                        : {})}
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
                      {label}
                    </button>
                  );
                })}
                {msg.schreibenDraft && onExportSchreibenDocx ? (
                  <button
                    type="button"
                    disabled={isLoading || exportingDocxId === msg.id}
                    onClick={() => void handleExportSchreibenDocx(msg)}
                    className={quietBtn}
                    title="Als Word-Dokument in der Akte ablegen und im Portal öffnen"
                    aria-label="Als DOCX"
                  >
                    {exportingDocxId === msg.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <FilePlus className="h-3 w-3" />
                    )}
                    Als DOCX
                  </button>
                ) : null}
              </div>
            ) : null}
            {/* MEMCORE Modul B: Daumen hoch = 2-Schritt-Speichern ins Gedächtnis (Dialog im Parent, §7.7).
                Eigener Name — „In Akte speichern“ ist die Schreiben-Datei in boundAkte, nicht BRAIN. */}
            {enableMemorySave && onMemorySaveRequest && msg.role === 'bot' && i > 0 && msg.text.trim() ? (
              <div className="flex w-full justify-start pl-8">
                <button
                  type="button"
                  onClick={() => onMemorySaveRequest({ messageId: msg.id ?? `idx-${i}`, text: schreibenSourceText(msg) })}
                  className={`mt-0.5 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] transition-colors hover:bg-white/[0.05] ${hauptLook ? 'text-violet-300/80 hover:text-violet-100' : 'text-slate-600 hover:text-blue-300'}`}
                  title="Antwort im Anwalts-Gedächtnis speichern (mit Bestätigung)"
                  aria-label={MEMCORE_SAVE_LABEL}
                >
                  <ThumbsUp className="h-3 w-3" />
                  {MEMCORE_SAVE_LABEL}
                </button>
              </div>
            ) : null}
            {/* Stream 4: „Merken“ unter Anwalts-Nachricht → Fall-Eckdaten im Thread pinnen (Auto-Vorbefüllung). */}
            {onPinFact && msg.role === 'user' && msg.text.trim() ? (() => {
              const pinKey = msg.id ?? `idx-${i}`;
              const isPinned = pinnedMsgIds.has(pinKey);
              return (
                <div className="flex w-full justify-end pr-1">
                  <button
                    type="button"
                    disabled={isPinned}
                    onClick={() => {
                      onPinFact(msg.text);
                      setPinnedMsgIds(prev => new Set(prev).add(pinKey));
                    }}
                    className={`mt-0.5 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] transition-colors ${
                      isPinned
                        ? 'text-emerald-400 cursor-default'
                        : hauptLook
                          ? 'text-violet-300/80 hover:bg-violet-600/20 hover:text-violet-100'
                          : 'text-slate-600 hover:bg-white/[0.05] hover:text-blue-300'
                    }`}
                    title={isPinned ? 'Als Fall-Eckdaten gemerkt' : 'Fall-Eckdaten merken — ARKO befüllt spätere Entwürfe damit vor'}
                    aria-label="Fall-Eckdaten merken"
                  >
                    <BookMarked className="h-3 w-3" />
                    {isPinned ? 'Gemerkt' : 'Merken'}
                  </button>
                </div>
              );
            })() : null}
          </div>
          );
        })}
        {isLoading && (
          <div className="flex justify-start items-start">
            {/* Probe / WIP Mascot v4 — Loading-Bubble */}
            <ArkoMascotAvatar sizeClassName="w-6 h-6" className="mr-2 mt-1" />
            <ArkoThinkingProgress
              context={
                thinkingContext ?? {
                  flow: 'webhook',
                  chatInput: pendingSendTextRef.current ?? '',
                }
              }
              variant={isCursor ? 'cursor' : 'portal'}
              compact={compactChrome}
            />
          </div>
        )}
        {hauptLook ? (
          <div aria-hidden data-testid="portal-haupt-scroll-tail" style={{ height: hauptTailPx }} />
        ) : null}
      </div>

      {documentContextEnabled && synthesisDocumentLabels ? (
        <div
          className={`flex-shrink-0 ${compactChrome ? 'px-2 pb-1' : 'px-3 pb-1.5'}`}
          role="status"
          aria-live="polite"
        >
          <p className="text-[11px] text-violet-300/90 truncate">
            ARKO nutzt: {synthesisDocumentLabels}
          </p>
        </div>
      ) : null}

      {/* Quick Actions — Tab „Schnell“ (Dokument) | „Recht“ (LEXCORE) */}
      {showQuickActions && !isCursor && (
        <PortalChipBar
          ref={chipBarRef}
          accent={resolvedChipAccent}
          legacyQaClassName={isCursor ? CURSOR_PORTAL.chip : accent.qa}
          compactChrome={compactChrome}
          className={compactChrome ? '!px-2 !pt-1' : ''}
          schnellContent={
            <>
              {documentQuickActions.map(action =>
                usePortalChips ? (
                  <PortalChip
                    key={action.label}
                    label={action.label}
                    icon={action.icon}
                    accent={resolvedChipAccent}
                    selected={pendingIntent === action.intent}
                    title={ARKO_TOOL_HINTS[action.label]}
                    onClick={() => applyQuickAction(action)}
                  />
                ) : (
                  <button
                    key={action.label}
                    onClick={() => applyQuickAction(action)}
                    type="button"
                    title={ARKO_TOOL_HINTS[action.label]}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${accent.qa}`}
                  >
                    <action.icon className="w-3 h-3" />
                    {action.label}
                  </button>
                ),
              )}
              {enableContractIntent && onContractGenerate && (
                usePortalChips ? (
                  <PortalChip
                    key="Vertrag erstellen"
                    label="Vertrag erstellen"
                    icon={FileSignature}
                    accent={resolvedChipAccent}
                    title={ARKO_TOOL_HINTS['Vertrag erstellen']}
                    aria-expanded={contractAskOpen}
                    onClick={startContractFromChip}
                  />
                ) : (
                  <button
                    key="Vertrag erstellen"
                    onClick={startContractFromChip}
                    type="button"
                    title={ARKO_TOOL_HINTS['Vertrag erstellen']}
                    aria-expanded={contractAskOpen}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${accent.qa}`}
                  >
                    <FileSignature className="w-3 h-3" />
                    Vertrag erstellen
                  </button>
                )
              )}
              {memcoreRecall && (
                usePortalChips ? (
                  <PortalChip
                    key={MEM_RECALL_CHIP_LABEL}
                    label={MEM_RECALL_CHIP_LABEL}
                    icon={Brain}
                    accent={resolvedChipAccent}
                    selected={pendingMemRecall}
                    title={MEM_RECALL_CHIP_HINT}
                    onClick={applyMemRecallChip}
                  />
                ) : (
                  <button
                    key={MEM_RECALL_CHIP_LABEL}
                    onClick={applyMemRecallChip}
                    type="button"
                    title={MEM_RECALL_CHIP_HINT}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${accent.qa} ${
                      pendingMemRecall ? 'ring-1 ring-blue-400/40' : ''
                    }`}
                  >
                    <Brain className="w-3 h-3" />
                    {MEM_RECALL_CHIP_LABEL}
                  </button>
                )
              )}
            </>
          }
          rechtContent={
            <>
              {showRechtsfrageQuickAction && (
                usePortalChips ? (
                  <PortalChip
                    key={RECHTSFRAGE_ACTION.label}
                    label={RECHTSFRAGE_ACTION.label}
                    icon={RECHTSFRAGE_ACTION.icon}
                    accent={resolvedChipAccent}
                    selected={pendingIntent === RECHTSFRAGE_ACTION.intent}
                    title={ARKO_TOOL_HINTS[RECHTSFRAGE_ACTION.label]}
                    onClick={() => applyQuickAction(RECHTSFRAGE_ACTION)}
                  />
                ) : (
                  <button
                    key={RECHTSFRAGE_ACTION.label}
                    onClick={() => applyQuickAction(RECHTSFRAGE_ACTION)}
                    type="button"
                    title={ARKO_TOOL_HINTS[RECHTSFRAGE_ACTION.label]}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${accent.qa}`}
                  >
                    <RECHTSFRAGE_ACTION.icon className="w-3 h-3" />
                    {RECHTSFRAGE_ACTION.label}
                  </button>
                )
              )}
              {showLegalChip && (
                usePortalChips ? (
                  <PortalChip
                    key="Als Rechtsfrage prüfen?"
                    label="Als Rechtsfrage prüfen?"
                    icon={Scale}
                    accent={resolvedChipAccent}
                    title={ARKO_TOOL_HINTS['Als Rechtsfrage prüfen?']}
                    onClick={recheckAsLegalQuestion}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={recheckAsLegalQuestion}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition-colors ${accent.qa}`}
                    title="Prüft die letzte Frage noch einmal im Normenbestand."
                  >
                    <Scale className="w-3 h-3" />
                    Als Rechtsfrage prüfen?
                  </button>
                )
              )}
            </>
          }
        />
      )}

      {/* Input — Cursor: Composer unten mit integrierten Modus-/Werkzeuge-Reitern */}
      {isCursor ? (
        <div className={`flex-shrink-0 ${compactChrome ? 'pb-2' : 'pb-4'}`}>
          {contractTypeAsk}
          <ArkoCursorComposer
            ref={composerRef}
            compact={compactChrome}
            placeholder={chatInputPlaceholder}
            layout="bottom"
            autoFocus={false}
            value={input}
            onChange={setInput}
            showToolsTray={showQuickActions}
            toolsBar={cursorToolsBar}
            isLoading={isLoading}
            onCancel={cancelRequest}
            attachedParagraphs={attachedParagraphs}
            onAttachedParagraphsChange={handleAttachedParagraphsChange}
            onParagraphLookup={handleParagraphLookup}
            onJudicatureLookup={handleJudicatureLookup}
            onWebSearchLookup={handleWebSearchLookup}
            relatedParagraphs={relatedParagraphs}
            contextMeter={arkoContextMeter}
            onContinueAkte={onContinueAkte}
            savedModeId={hubModeId}
            onModeCommit={onHubModeCommit}
            appearance={appearance}
            onSubmit={(submitText, mode, attachmentFiles) => {
              void handleComposerSubmit(submitText, mode, attachmentFiles);
            }}
          />
          <ArkoChatAiActNotice compact={compactChrome} />
        </div>
      ) : (
      <form
        onSubmit={handleSubmit}
        className={`${PORTAL_CHAT.inputBar} ${compactChrome ? 'p-2' : 'p-3'}`}
      >
        {contractTypeAsk}
        <div className="flex items-end gap-1.5">
          <textarea
            ref={textareaRef}
            data-portal-arko-input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={chatInputPlaceholder}
            rows={1}
            className={`${PORTAL_CHAT.input} ${
              compactChrome ? 'px-3 py-1.5 text-xs rounded-lg' : 'px-4 py-2.5 text-sm rounded-xl'
            }`}
            style={{ minHeight: compactChrome ? '34px' : '42px' }}
          />
          {isLoading ? (
            <button
              type="button"
              onClick={cancelRequest}
              className={`${accent.btn} flex-shrink-0 self-end ${
                compactChrome ? 'p-2 rounded-lg' : 'p-2.5 rounded-xl'
              }`}
              aria-label="Anfrage abbrechen"
              title="Abbrechen"
            >
              <Square className="w-3.5 h-3.5 fill-current" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className={`${accent.btn} disabled:bg-slate-800 disabled:text-slate-500 disabled:cursor-not-allowed disabled:ring-0 disabled:shadow-none flex-shrink-0 self-end ${
                compactChrome ? 'p-2 rounded-lg' : 'p-2.5 rounded-xl'
              }`}
              aria-label="Nachricht senden"
            >
              <Send className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {showQuickActions && !compactChrome && (
          <p className="text-[10px] text-slate-600 mt-1.5 px-1">Enter senden · Strg+Enter Zeilenumbruch</p>
        )}
      </form>
      )}
      {!isCursor && <ArkoChatAiActNotice compact={compactChrome} />}
    </div>
  );
};

export default InlineChat;
